import { useState, useRef, useEffect, useLayoutEffect, useCallback, memo } from 'react'
import { isPhoneViewport, isPhoneDevice } from './hooks/useIsPhone'
import { useDualWallet } from './DualWalletContext'
import { getAllTokenHoldings, getTokenName, getSOLBalance, executeSell } from '../utils/jupiter'
import { useTradingContext } from './TradingContext'
import { useTradeDefaults } from './TradeDefaultsContext'
import { solPositionPnl, futuresPositionPnl } from '../utils/pnlUtils'
import KnurledWheel from './KnurledWheel'
import MarketLimitToggle from './MarketLimitToggle'
import { BASE as SCHWAB_BASE, KEY as SCHWAB_ACCT, OWNER as SCHWAB_OWNER } from './SchwabAccountBadge'
import { getLpPositions, ensureLpPositions, refreshLpPositions, onLpChange } from '../utils/meteoraLp'
import MeteoraLpPanel, { MeteoraV2Actions, MeteoraDlmmActions } from './MeteoraLpPanel'
import { createPortal } from 'react-dom'
import { WalletMapIcon, WalletMapCanvas } from './WalletMapPanel'
import { useOrderEntry, usePushOrder } from './hooks/usePushOrder'
import OrderPicker, { OrderStatusLine } from './OrderPicker'   // order-picker-20261021
import { ORDERS } from './OrderEntryDrawer'
import { useRecipeOrder } from './hooks/useRecipeOrder'   // grok-orders-20261021
import { listRecipes, deleteRecipe, updateRecipeSetting } from '../utils/orderRecipes'
import { isPaperActive } from '../utils/paperTrade'   // paperclose-20261027
import { cmeInfo, stkInfo } from '../utils/chartUtils'
import { CME_PAPER_EVENT, cmeQty as loadCmeQty, setCmeQty as saveCmeQty, cmePosition, cmeRealized, cmeUnrealized, cmeFill, cmeFlatten, pointValue, cmeAccount, cmeCanAfford, cmeResetAccount, CME_MARGIN_PER_CT } from '../utils/cmePaper'   // cmepaper-20261101
import { setOrderStatus, setMyWallet } from '../utils/signalBus'

const DEFAULT_PUMP_SUPPLY = 1_000_000_000

// ─── Liq arm trigger (longpress-liq-20261017) ───
// A long-pressed BUY/SELL fires on the next Liq reaction at or under this
// many seconds, in its own direction. Reactions come from the chart's
// OrderflowBot liq tracker via 'solcheddar-liq-reaction'.
const LIQ_FIRE_MAX_SEC = 3
const LIQ_ARM_LABEL = `Liquidity`

// ─── Robot-head mark (same glyph as the chart's BotBadge) ───
// stroke/fill ride currentColor so the CSS state classes drive the tint.
const ArdBotIcon = ({ size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round"
    strokeLinejoin="round" aria-hidden="true" style={{ display: 'block' }}>
    <path d="M12 2v3" />
    <circle cx="12" cy="2" r="1" fill="currentColor" stroke="none" />
    <rect x="4" y="7" width="16" height="12" rx="3" />
    <circle cx="9" cy="13" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="15" cy="13" r="1.4" fill="currentColor" stroke="none" />
  </svg>
)

// ─── Editable readout: the value under each control ───
// Click-to-type entry layered on the knob/slider WITHOUT touching their
// drag/wheel behavior — the readout is a sibling BELOW the pointer surface,
// so entering edit mode can never start a drag. A dotted underline marks it
// editable at rest; clicking swaps in a bare input with the identical
// footprint and typography (select-all so the first keystroke replaces).
// Enter/blur commits, Escape cancels, junk keeps the current value. Typed
// values clamp to [min,max] and round to `step`, but deliberately SKIP
// snapPoints — snapping is a drag affordance; the whole point of typing is
// a value the presets don't offer. inputMode="decimal" summons the phone's
// numeric keypad instead of the full keyboard (enterKeyHint="done" so the
// return key reads right on mobile).
function EditableReadout({ value, display, min, max, step, onChange, color }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (editing) { inputRef.current?.focus(); inputRef.current?.select() }
  }, [editing])

  const commit = () => {
    setEditing(false)
    const n = parseFloat(draft)
    if (!Number.isFinite(n)) return                 // junk → keep current
    let v = n
    if (step) v = Math.round(v / step) * step
    v = Math.max(min, Math.min(max, parseFloat(Number(v).toPrecision(8))))
    if (v !== value) onChange(v)
  }

  const base = { fontSize: '9px', fontWeight: 600, color, fontFamily: "'JetBrains Mono', monospace", lineHeight: 1 }

  // Ghost-slot layout so ACTIVATION NEVER MOVES ANYTHING: the formatted
  // display renders unconditionally and owns the readout's footprint (its
  // intrinsic width, plus a fixed 12px slot height that already includes
  // room for the underline/border). Entering edit mode only hides the
  // ghost's ink (visibility, not display — the box stays) and overlays the
  // input absolutely inside that same slot. Nothing about the column's
  // geometry changes on click; a draft longer than the slot scrolls inside
  // the input instead of pushing siblings.
  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        if (!editing) { setDraft(String(value)); setEditing(true) }
      }}
      title={editing ? undefined : 'click to type a value'}
      style={{
        position: 'relative',
        height: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: editing ? 'default' : 'text',
      }}
    >
      <div style={{
        ...base,
        visibility: editing ? 'hidden' : 'visible',
        textDecoration: 'underline dotted',
        textDecorationColor: color + '55',
        textUnderlineOffset: '2px',
        whiteSpace: 'nowrap',
      }}>{display}</div>
      {editing && (
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          enterKeyHint="done"
          value={draft}
          onChange={(e) => setDraft(e.target.value.replace(/[^0-9.]/g, ''))}
          onBlur={commit}
          onKeyDown={(e) => {
            e.stopPropagation()
            if (e.key === 'Enter') commit()
            else if (e.key === 'Escape') setEditing(false)
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            ...base,
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            background: 'transparent',
            border: 'none',
            borderBottom: `1px solid ${color}`,
            outline: 'none',
            padding: 0,
            textAlign: 'center',
          }}
        />
      )}
    </div>
  )
}

// ─── Rotary Knob (SVG, drag + wheel) ───
const KNOB_START_ANGLE = 135  // bottom-left
const KNOB_END_ANGLE = 405    // bottom-right (135 + 270)
const KNOB_SWEEP = KNOB_END_ANGLE - KNOB_START_ANGLE

// Connected wallet's interaction map, floating over the header button: no
// panel chrome, transparent, just the map. Closes on a second click of the
// icon, Esc, or a press outside (a drag that starts on the map never closes
// it). Sized to the viewport and kept on screen, phone included.
// Popover size is fixed (capped by the viewport), so the position comes
// straight from the button rect: computed once on open and again only on
// window resize. No measuring, no per-render re-placement.
function placeHoldingsMap(anchor) {
  const a = anchor?.getBoundingClientRect()
  if (!a) return null
  const vw = window.innerWidth, vh = window.innerHeight
  const w = Math.min(380, vw - 16), h = Math.min(340, Math.round(vh * 0.6), vh - 16)
  // Centre the map on the button horizontally; ~80% of it above the
  // button's centre, 20% below. Clamp into the viewport (8px margin),
  // shifting only as far as needed at the edges.
  const cx = a.left + a.width / 2, cy = a.top + a.height / 2
  const left = Math.round(Math.max(8, Math.min(cx - w / 2, vw - w - 8)))
  const top = Math.round(Math.max(8, Math.min(cy - 0.80 * h, vh - h - 8)))
  return { left, top, w, h }
}
const MAP_FILL = { width: '100%', height: '100%' }

const HoldingsMapPopover = memo(function HoldingsMapPopover({ address, anchorRef, onClose }) {
  const popRef = useRef(null)
  const [box, setBox] = useState(() => placeHoldingsMap(anchorRef.current))
  useEffect(() => {
    const onResize = () => setBox(placeHoldingsMap(anchorRef.current))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [anchorRef])
  useEffect(() => {
    const down = (e) => {
      if (popRef.current?.contains(e.target) || anchorRef.current?.contains(e.target)) return
      onClose()
    }
    const key = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('pointerdown', down, true)
    window.addEventListener('keydown', key)
    return () => { document.removeEventListener('pointerdown', down, true); window.removeEventListener('keydown', key) }
  }, [anchorRef, onClose])
  if (!box) return null
  return createPortal(
    <div ref={popRef} className="holdings-map-pop" role="dialog" aria-label="Wallet map"
      style={{ left: box.left, top: box.top, width: box.w, height: box.h }}>
      <WalletMapCanvas key={address} address={address} transparent style={MAP_FILL} width={box.w} height={box.h} />
    </div>,
    document.body,
  )
})

function RotaryKnob({ value, min, max, step, snapPoints, onChange, label, color, formatValue, size = 52 }) {
  const knobRef = useRef(null)
  const dragging = useRef(false)
  const dragStart = useRef({ y: 0, val: 0 })
  const gradId = useRef(`knobGrad_${Math.random().toString(36).slice(2, 8)}`).current

  const pct = max > min ? (value - min) / (max - min) : 0
  const clampedPct = Math.max(0, Math.min(1, pct))

  const snapToNearest = useCallback((raw) => {
    if (!snapPoints) return raw
    let best = snapPoints[0]
    for (const sp of snapPoints) {
      if (Math.abs(sp - raw) < Math.abs(best - raw)) best = sp
    }
    return best
  }, [snapPoints])

  const clampValue = useCallback((raw) => {
    if (snapPoints) raw = snapToNearest(raw)
    else if (step) raw = Math.round(raw / step) * step
    return Math.max(min, Math.min(max, parseFloat(Number(raw).toPrecision(8))))
  }, [min, max, step, snapPoints, snapToNearest])

  // Drag: vertical mouse movement controls value
  const handlePointerDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragging.current = true
    dragStart.current = { y: e.clientY, val: value }
    knobRef.current?.setPointerCapture?.(e.pointerId)
  }, [value])

  const handlePointerMove = useCallback((e) => {
    if (!dragging.current) return
    e.preventDefault()
    const dy = dragStart.current.y - e.clientY // up = positive
    const sensitivity = (max - min) / 120
    const raw = dragStart.current.val + dy * sensitivity
    onChange(clampValue(raw))
  }, [max, min, onChange, clampValue])

  const handlePointerUp = useCallback((e) => {
    dragging.current = false
    knobRef.current?.releasePointerCapture?.(e.pointerId)
  }, [])

  // Wheel
  useEffect(() => {
    const el = knobRef.current
    if (!el) return
    const handler = (e) => {
      e.preventDefault()
      const dir = e.deltaY < 0 ? 1 : -1
      if (snapPoints) {
        const idx = snapPoints.indexOf(snapToNearest(value))
        const next = Math.max(0, Math.min(snapPoints.length - 1, idx + dir))
        onChange(snapPoints[next])
      } else {
        const s = step || ((max - min) / 100)
        onChange(clampValue(value + dir * s))
      }
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  })

  const display = formatValue ? formatValue(value) : value

  // SVG arc math
  const r = size / 2
  const cx = r, cy = r
  const arcR = r - 4 // arc radius (inset from edge)
  const knobR = r - 8 // inner knob circle radius

  const angleForPct = (p) => KNOB_START_ANGLE + p * KNOB_SWEEP
  const curAngle = angleForPct(clampedPct)

  const polarToXY = (angle, radius) => {
    const rad = (angle - 90) * Math.PI / 180
    return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) }
  }

  // Background track arc (full sweep)
  const trackStart = polarToXY(KNOB_START_ANGLE, arcR)
  const trackEnd = polarToXY(KNOB_END_ANGLE, arcR)
  const trackPath = `M ${trackStart.x} ${trackStart.y} A ${arcR} ${arcR} 0 1 1 ${trackEnd.x} ${trackEnd.y}`

  // Active arc
  const activeEnd = polarToXY(curAngle, arcR)
  const largeArc = (curAngle - KNOB_START_ANGLE) > 180 ? 1 : 0
  const activePath = clampedPct > 0.005
    ? `M ${trackStart.x} ${trackStart.y} A ${arcR} ${arcR} 0 ${largeArc} 1 ${activeEnd.x} ${activeEnd.y}`
    : ''

  // Notch indicator on knob edge
  const notchOuter = polarToXY(curAngle, knobR - 1)
  const notchInner = polarToXY(curAngle, knobR - 7)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
      <div style={{ fontSize: '8px', fontWeight: 700, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
      <div
        ref={knobRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{ position: 'relative', width: size, height: size, cursor: 'pointer', touchAction: 'none' }}
      >
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Outer shadow ring */}
          <circle cx={cx} cy={cy} r={r - 2} fill="none" stroke="#0a0a0e" strokeWidth="3" />
          {/* Track arc (dark) */}
          <path d={trackPath} fill="none" stroke="#1d1d24" strokeWidth="3" strokeLinecap="round" />
          {/* Active arc (colored) */}
          {activePath && (
            <path d={activePath} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 3px ${color}88)` }} />
          )}
          {/* Knob body */}
          <circle cx={cx} cy={cy} r={knobR} fill="#222231" />
          <circle cx={cx} cy={cy} r={knobR} fill={`url(#${gradId})`} />
          {/* Inner bevel */}
          <circle cx={cx} cy={cy} r={knobR - 1} fill="none" stroke="#393941" strokeWidth="0.5" />
          {/* Notch indicator */}
          <line x1={notchInner.x} y1={notchInner.y} x2={notchOuter.x} y2={notchOuter.y}
            stroke={color} strokeWidth="2" strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 2px ${color}aa)` }} />
          {/* Radial gradient for 3D look */}
          <defs>
            <radialGradient id={gradId} cx="40%" cy="35%">
              <stop offset="0%" stopColor="#2a2a35" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#0e0e12" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>
      <EditableReadout value={value} display={display} min={min} max={max} step={step} onChange={onChange} color={color} />
    </div>
  )
}

// ─── Vertical Slider (drag + wheel) ───
// Drop-in replacement for RotaryKnob: identical prop contract
// (value, min, max, step, snapPoints, onChange, label, color, formatValue,
// size). Vertical orientation so it occupies the same row footprint as the
// knobs it replaces. Track is full height; fill grows from the bottom.
function SliderControl({ value, min, max, step, snapPoints, onChange, label, color, formatValue, size = 52 }) {
  const trackRef = useRef(null)
  const dragging = useRef(false)

  const pct = max > min ? (value - min) / (max - min) : 0
  const clampedPct = Math.max(0, Math.min(1, pct))

  const snapToNearest = useCallback((raw) => {
    if (!snapPoints) return raw
    let best = snapPoints[0]
    for (const sp of snapPoints) {
      if (Math.abs(sp - raw) < Math.abs(best - raw)) best = sp
    }
    return best
  }, [snapPoints])

  const clampValue = useCallback((raw) => {
    if (snapPoints) raw = snapToNearest(raw)
    else if (step) raw = Math.round(raw / step) * step
    return Math.max(min, Math.min(max, parseFloat(Number(raw).toPrecision(8))))
  }, [min, max, step, snapPoints, snapToNearest])

  // Map a clientY within the track to a value. The cap center travels only
  // within [half, trackH-half], so map against that inset range for 1:1
  // feel (top = max, bottom = min).
  const valueFromClientY = useCallback((clientY) => {
    const el = trackRef.current
    if (!el) return value
    const rect = el.getBoundingClientRect()
    const CAP_H = 26 // kept in sync with the render geometry below
    const usable = rect.height - CAP_H
    const y = clientY - rect.top - CAP_H / 2
    const p = 1 - Math.max(0, Math.min(1, usable > 0 ? y / usable : 0))
    const raw = min + p * (max - min)
    return clampValue(raw)
  }, [min, max, clampValue, value])

  const handlePointerDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    dragging.current = true
    trackRef.current?.setPointerCapture?.(e.pointerId)
    onChange(valueFromClientY(e.clientY)) // jump-to-click
  }, [onChange, valueFromClientY])

  const handlePointerMove = useCallback((e) => {
    if (!dragging.current) return
    e.preventDefault()
    onChange(valueFromClientY(e.clientY))
  }, [onChange, valueFromClientY])

  const handlePointerUp = useCallback((e) => {
    dragging.current = false
    trackRef.current?.releasePointerCapture?.(e.pointerId)
  }, [])

  // Wheel — same stepping semantics as the knob.
  useEffect(() => {
    const el = trackRef.current
    if (!el) return
    const handler = (e) => {
      e.preventDefault()
      const dir = e.deltaY < 0 ? 1 : -1
      if (snapPoints) {
        const idx = snapPoints.indexOf(snapToNearest(value))
        const next = Math.max(0, Math.min(snapPoints.length - 1, idx + dir))
        onChange(snapPoints[next])
      } else {
        const s = step || ((max - min) / 100)
        onChange(clampValue(value + dir * s))
      }
    }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  })

  const display = formatValue ? formatValue(value) : value

  // Console-fader geometry. Thin track runs the full height; colored fill
  // rises from the bottom up to the cap (the "indicator color in the line
  // below the slider"). The cap is a wide ribbed rectangle with a bright
  // center line, like a mixing-desk fader.
  const trackW = 4
  const trackH = size + 18           // a touch taller so the cap has travel
  const capW = Math.max(26, size * 0.62)
  const capH = 26
  // Horizontal center of the groove region (container is capW+6 wide, so
  // this is its true center — slider sits centered in its section).
  const trackCenterX = (capW + 6) / 2
  // Center of the cap, in px from the top. Clamp so the cap stays fully on
  // the track at both ends: travels from half (top=max) to trackH-half
  // (bottom=min).
  const half = capH / 2
  const capCenter = half + (1 - clampedPct) * (trackH - capH)
  const fillTop = capCenter // fill ends where the cap sits

  // Ribbed cap: a stack of thin horizontal bands, bright line in the middle.
  const ribs = []
  const RIB_COUNT = 9
  for (let i = 0; i < RIB_COUNT; i++) {
    const mid = i === (RIB_COUNT - 1) / 2
    const t = i / (RIB_COUNT - 1)
    // Greyscale gradient top→bottom for a metallic look.
    // Dark greyscale ridge. Range ~18..48 so the cap reads as a dark
    // metallic block rather than a light grey one.
    const g = Math.round(18 + 30 * Math.abs(0.5 - t) * 2)
    ribs.push(
      <div key={i} style={{
        flex: 1,
        background: mid ? '#9a9aa2' : `rgb(${g},${g},${g + 4})`,
        boxShadow: mid ? `0 0 3px ${color}` : 'none',
      }} />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
      <div style={{ fontSize: '8px', fontWeight: 700, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
      <div
        ref={trackRef}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        style={{
          position: 'relative',
          width: capW + 6, // groove region only — container re-centers in section
          height: trackH,
          display: 'flex',
          cursor: 'pointer',
          touchAction: 'none',
        }}
      >
        {/* Tick marks on BOTH sides of the channel, running the FULL track
            height. 6 major marks with a minor between each (11 rows total:
            even = major, odd = minor). These sit on a layer BELOW the cap
            (zIndex 1), so the cap travels over them like a real fader.
            Decorative — pointerEvents none. */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: trackH,
          pointerEvents: 'none',
          zIndex: 1,
        }}>
          {Array.from({ length: 11 }).map((_, i) => {
            // Skip the very top and bottom rows so the marks stop short of
            // the track ends rather than running all the way to the edge.
            if (i === 0 || i === 10) return null
            const major = i % 2 === 0
            const top = (i / 10) * 100
            const len = major ? 7 : 4   // major marks longer
            const shade = major ? '#666' : '#4a4a52'
            const gap = trackW / 2 + 3  // inner edge: distance from track center
            const majorLen = 7          // defines the outer edge all marks align to
            return (
              <div key={i} style={{ position: 'absolute', top: `${top}%`, left: 0, right: 0, height: 1, transform: 'translateY(-50%)' }}>
                {/* left mark — outer edge aligned (flush on the outside) */}
                <div style={{
                  position: 'absolute',
                  left: trackCenterX - gap - majorLen,
                  width: len, height: 1, background: shade,
                }} />
                {/* right mark — outer edge aligned (flush on the outside) */}
                <div style={{
                  position: 'absolute',
                  left: trackCenterX + gap + (majorLen - len),
                  width: len, height: 1, background: shade,
                }} />
              </div>
            )
          })}
        </div>
        {/* Track + fill + cap, centered in the groove region. */}
        {/* Track groove (full height, neutral) */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: trackCenterX,
          transform: 'translateX(-50%)',
          width: trackW,
          height: trackH,
          background: '#15151b',
          borderRadius: trackW / 2,
          border: '1px solid #000',
          boxSizing: 'border-box',
          zIndex: 2,
        }} />
        {/* Colored indicator fill — fills the track from the bottom up to
            the cap. This is the value indicator that sits BELOW the cap. */}
        <div style={{
          position: 'absolute',
          top: fillTop,
          bottom: 0,
          left: trackCenterX,
          transform: 'translateX(-50%)',
          width: trackW,
          background: color,
          borderRadius: trackW / 2,
          boxShadow: `0 0 5px ${color}cc`,
          zIndex: 2,
        }} />
        {/* Ribbed fader cap — rides OVER the tick marks (zIndex 3). */}
        <div style={{
          position: 'absolute',
          top: capCenter,
          left: trackCenterX,
          transform: 'translate(-50%, -50%)',
          width: capW,
          height: capH,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid #000',
          boxShadow: '0 2px 5px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255, 255, 255, 0.06)',
          zIndex: 3,
        }}>
          {ribs}
        </div>
      </div>
      <EditableReadout value={value} display={display} min={min} max={max} step={step} onChange={onChange} color={color} />
    </div>
  )
}

// ─── Control wrapper: renders dial or slider, right-click to switch ───
// Shares the exact prop contract of RotaryKnob/SliderControl. `styleKind`
// selects which to render; `onContextMenu` bubbles a right-click up so the
// parent can open the dial/slider chooser for this section.
function ControlInput({ styleKind, onRequestMenu, ...props }) {
  const handleContextMenu = (e) => {
    e.preventDefault()
    e.stopPropagation()
    onRequestMenu?.(e.clientX, e.clientY)
  }
  return (
    <div onContextMenu={handleContextMenu}>
      {styleKind === 'dial'
        ? <RotaryKnob {...props} />
        : <SliderControl {...props} />}
    </div>
  )
}

// ─── Vertical Bar Gauge (volume-indicator style) ───
function BarGauge({ value, max, label, segments = 7, color = '#e8d44d' }) {
  const filled = max > 0 ? Math.round((value / max) * segments) : 0
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', minWidth: '26px' }}>
      <div style={{ fontSize: '7px', fontWeight: 700, color: '#555', letterSpacing: '0.05em', textTransform: 'uppercase', alignSelf: 'center' }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column-reverse', alignItems: 'flex-start', gap: '2px' }}>
        {Array.from({ length: segments }, (_, i) => {
          const progress = i / (segments - 1);
          const barWidth = 8 + Math.pow(progress, 3.14) * 20;   // ← tweak these two numbers

          return (
            <div key={i} style={{
              width: `${Math.round(barWidth)}px`,
              height: '5px',
              borderRadius: '2px 0px 5px 2px',
              background: i < filled ? color : '#24242e',
              boxShadow: i < filled ? `0 0 4px ${color}55` : 'none',
              transition: 'background 0.15s',
            }} />
          )
        })}
      </div>
      <div style={{ fontSize: '8px', fontWeight: 600, color: color + 'cc', fontFamily: "'JetBrains Mono', monospace", lineHeight: 1, alignSelf: 'center' }}>
        {typeof value === 'number' ? (value >= 10 ? value.toFixed(0) : value.toFixed(1)) : value}
      </div>
    </div>
  )
}

function getStoredPositions(mint) {
  try {
    return JSON.parse(localStorage.getItem(`solcheddar_positions_${mint}`) || '[]')
  } catch {}
  return []
}

// Get aggregated position data (for backward compatibility with single position displays)
function getStoredPosition(mint) {
  const positions = getStoredPositions(mint)
  if (positions.length === 0) return null
  if (positions.length === 1) return positions[0]
  // Aggregate multiple positions into one weighted average
  const totalTokens = positions.reduce((sum, p) => sum + (p.tokenAmount || 0), 0)
  if (totalTokens === 0) return positions[0]
  const weightedMcap = positions.reduce((sum, p) => sum + (p.entryMcap || 0) * (p.tokenAmount || 0), 0) / totalTokens
  return {
    ...positions[0],
    entryMcap: weightedMcap,
    tokenAmount: totalTokens,
    aggregated: true,
    positionCount: positions.length,
  }
}

// ─── Holding market data: ONE batched DexScreener read for every row ───
// Symbol, USD price, SOL price and market cap per mint, from the pair with
// the deepest liquidity (the old read took whichever pair came first). Cached
// per mint so a failed or partial refresh keeps the last good number for a
// while instead of showing $0, and so names no longer cost one request per
// row per refresh (getTokenName is only asked for mints the batch missed).
const MARKET_CACHE = new Map()      // mint -> { priceUsd, priceNative, mcapUsd, symbol, at }
const MARKET_STALE_MS = 5 * 60_000
const NAME_CACHE = new Map()        // mint -> symbol
const DEX_BATCH = 30                // DexScreener /tokens/ accepts up to 30 addresses

export async function fetchMarketBatch(mints) {
  const out = {}
  const fresh = {}
  for (let i = 0; i < mints.length; i += DEX_BATCH) {
    const chunk = mints.slice(i, i + DEX_BATCH)
    try {
      const res = await fetch(`https://api.dexscreener.com/latest/dex/tokens/${chunk.join(',')}`)
      if (!res.ok) continue
      const data = await res.json()
      for (const pair of (data.pairs || [])) {
        const mint = pair.baseToken?.address
        if (!mint || !chunk.includes(mint)) continue
        const liq = +pair.liquidity?.usd || 0
        if (fresh[mint] && fresh[mint].liq >= liq) continue
        fresh[mint] = {
          liq,
          priceUsd: parseFloat(pair.priceUsd) || 0,
          // priceNative is in the QUOTE token — only SOL-quoted pairs give SOL.
          priceNative: pair.quoteToken?.symbol === 'SOL' ? (parseFloat(pair.priceNative) || 0) : 0,
          mcapUsd: +pair.marketCap || +pair.fdv || 0,
          symbol: pair.baseToken?.symbol || null,
        }
      }
    } catch {}
  }
  const now = Date.now()
  for (const m of mints) {
    if (fresh[m]) {
      const { liq: _liq, ...rest } = fresh[m]
      MARKET_CACHE.set(m, { ...rest, at: now })
      if (rest.symbol) NAME_CACHE.set(m, rest.symbol)
    }
    const c = MARKET_CACHE.get(m)
    if (c && now - c.at < MARKET_STALE_MS) out[m] = c
  }
  return out
}

const fmtUsdMcap = (n) => {
  if (!(n > 0)) return '—'
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B'
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(2) + 'M'
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(1) + 'K'
  return '$' + n.toFixed(0)
}
const fmtSol = (n) => {
  if (!(n > 0)) return null
  if (n >= 100) return n.toFixed(0) + ' SOL'
  if (n >= 1) return n.toFixed(2) + ' SOL'
  return n.toFixed(3) + ' SOL'
}

/** Per-row details, every holding the same: balance, value (USD + SOL),
 *  market cap, average entry (from the local position record) and P/L.
 *  P/L uses the chart tag's convention (solPositionPnl: move in SOL terms,
 *  converted at the current SOL price), so a row and the tag agree. */
export function holdingDetails(t, solUsd) {
  const pos = getStoredPosition(t.mint)
  const supply = pos?.tokenSupply > 0 ? pos.tokenSupply : DEFAULT_PUMP_SUPPLY
  const priceUsd = t.priceUsd || 0
  const valueUsd = (t.uiAmount || 0) * priceUsd
  const valueSol = solUsd > 0 ? valueUsd / solUsd
    : (t.priceNative > 0 ? t.priceNative * (t.uiAmount || 0) : 0)
  // DEX market cap; else price × the supply recorded at entry. No guessed
  // 1B supply here — that is only true for pump coins.
  const mcapUsd = t.mcapUsd > 0 ? t.mcapUsd : (priceUsd > 0 && pos?.tokenSupply > 0 ? priceUsd * pos.tokenSupply : 0)
  const hasEntry = !!(pos && pos.entryMcap > 0)
  // entryMcap is SOL-denominated; USD at the SOL price of the entry.
  const entryMcapUsd = hasEntry && pos.entrySolPrice > 0 ? pos.entryMcap * pos.entrySolPrice : null
  let pnlUsd = t.pnlUsd ?? null
  let pnlPct = t.pnlPct ?? null
  if (hasEntry && priceUsd > 0 && solUsd > 0) {
    const r = solPositionPnl({
      entryMcap: pos.entryMcap,
      currentMcap: (priceUsd / solUsd) * supply,
      tokenAmount: t.uiAmount || 0,
      tokenSupply: supply,
      solPriceUsd: solUsd,
    })
    if (r.pnlUsd != null) { pnlUsd = r.pnlUsd; pnlPct = r.pnlPct }
  }
  return { amount: t.uiAmount || 0, valueUsd, valueSol, mcapUsd, hasEntry, entryMcapUsd, pnlUsd, pnlPct }
}

/** The two detail lines every holding row shows (compact, same classes). */
function HoldingDetailRows({ d, formatAmount, formatUsd, formatPnl }) {
  const sol = fmtSol(d.valueSol)
  const hasPnl = d.pnlUsd != null && Number.isFinite(d.pnlUsd)
  return (
    <>
      <div className="wallet-position-row">
        <span className="wallet-position-details">{formatAmount(d.amount)} tokens</span>
        <span className="wallet-position-pnl pos">
          {formatUsd(d.valueUsd)}{sol && <span style={{ color: '#666', fontWeight: 400 }}> · {sol}</span>}
        </span>
      </div>
      <div className="wallet-position-row">
        <span className="wallet-position-details" style={{ color: '#555' }}
          title={d.hasEntry ? 'Average entry from this app’s trade record' : 'No entry recorded for this token in this app'}>
          mcap {fmtUsdMcap(d.mcapUsd)} · entry {d.hasEntry && d.entryMcapUsd ? fmtUsdMcap(d.entryMcapUsd) : '—'}
        </span>
        {hasPnl ? (
          <span className={`wallet-position-pnl ${d.pnlUsd >= 0 ? 'pos' : 'neg'}`}>
            {d.pnlPct != null && Number.isFinite(d.pnlPct) ? `${d.pnlPct >= 0 ? '+' : ''}${d.pnlPct.toFixed(1)}% ` : ''}{formatPnl(d.pnlUsd)}
          </span>
        ) : (
          <span className="wallet-position-details" style={{ color: '#555' }}>—</span>
        )}
      </div>
    </>
  )
}

function getEntryUsdPrice(pos) {
  if (!pos || !pos.entryMcap || !pos.entrySolPrice) return null
  // Use stored tokenSupply if available, otherwise fall back to default
  const supply = pos.tokenSupply > 0 ? pos.tokenSupply : DEFAULT_PUMP_SUPPLY
  return (pos.entryMcap / supply) * pos.entrySolPrice
}

// ─── Close-fold timing ───
// ONE source of truth. These feed BOTH the @keyframes below and the JS safety
// timeout — if they drifted apart, the belt would fire mid-fold and chop the
// animation off halfway. (It did: the belt was 500ms against a 620ms fold.)
const COLLAPSE_MS   = 140    // the wind-up. Faster than the idle 220ms collapse —
                             // during a close it's a beat, not a feature.
const FOLD_DELAY_MS = 150    // fold starts AFTER the collapse has fully landed.
const FOLD_DUR_MS   = 520    // 9 creases. Tune THIS if it drags.
const FOLD_TOTAL_MS = FOLD_DELAY_MS + FOLD_DUR_MS
const GHOST_MS      = 380    // one outline's whole life: snap on, hold, fade.
const CLOSE_TOTAL_MS = FOLD_TOTAL_MS + GHOST_MS   // last ghost fades → unmount

// Each crease removes exactly ONE triangle. These are those nine, in order, in
// the same 0-100 space the clip-path polygons use — so every ghost traces
// precisely the piece that just left. `at` is that crease's moment as a fraction
// of the fold, which becomes the outline's animation-delay.
const STATION_POS_KEY = 'solcheddar_station_pos'
// Only dragOffset is persisted, not `pinned`. Nothing sets pinned false any more
// (the two places that did were the close handlers, and they were the bug), so
// it's structurally always true — and storing a stale `false` would arm the
// "reset drag offset when opening fresh (unless pinned)" effect further down and
// silently throw the position away on the next open. One less thing to be wrong.
function readStationOffset() {
  try {
    const v = JSON.parse(sessionStorage.getItem(STATION_POS_KEY))
    if (v && typeof v.x === 'number' && typeof v.y === 'number') return v
  } catch {}
  return { x: 0, y: 0 }   // home: the pinned base, (10, 175)
}

const FOLD_GHOSTS = [
  { pts: '0,0 34,0 0,50',        at: 0.165 },   // cycle 1  top corner
  { pts: '0,100 34,100 0,50',    at: 0.291 },   //          bottom corner
  { pts: '34,0 0,50 34,100',     at: 0.406 },   //          the point
  { pts: '34,0 67,0 34,50',      at: 0.514 },   // cycle 2  top corner
  { pts: '34,100 67,100 34,50',  at: 0.618 },   //          bottom corner
  { pts: '67,0 34,50 67,100',    at: 0.717 },   //          the point
  { pts: '67,0 100,0 67,50',     at: 0.814 },   // cycle 3  top corner
  { pts: '67,100 100,100 67,50', at: 0.908 },   //          bottom corner
  { pts: '100,0 67,50 100,100',  at: 1.000 },   //          the point
]

// One-line LP readout attached to a token row. IN/OUT is the number that
// matters on a mover: a DLMM position stops earning the moment price leaves
// its bins, and on a hot coin that happens fast.
// DAMM v2 lines carry Claim / Remove; DLMM stays read-only here (managed on
// meteora.ag) — hence the link instead of buttons.
const lpKey = (lp, i) => lp.position || lp.pool || i

function LpAmounts({ lp, formatUsd, formatAmount }) {
  if (lp.valueUsd != null) return formatUsd(lp.valueUsd)
  if (lp.amountX != null) return `${formatAmount(lp.amountX)} ${lp.symX} + ${formatAmount(lp.amountY)} ${lp.symY}`
  return '—'
}

function LpManage({ lp }) {
  if (lp.kind === 'v2') return <MeteoraV2Actions lp={lp} />
  if (!lp.pool) return null
  // DLMM: in-app Claim / Close (lp-dlmm-close-20261015); small Meteora link kept inside.
  return <MeteoraDlmmActions lp={lp} />
}

function lpErrorText(s) {
  if (!s) return null
  const parts = []
  if (s.status === 'blocked') parts.push('DLMM LP data blocked (CORS / network)')
  else if (String(s.status).startsWith('http_')) parts.push(`DLMM LP data unavailable (HTTP ${String(s.status).slice(5)})`)
  else if (s.status === 'unparsed') parts.push('DLMM LP data came back in an unexpected format')
  if (s.v2Status === 'error') parts.push('DAMM v2 positions failed to load')
  return parts.length ? parts.join(' · ') : null
}

function LpLine({ lp, formatUsd, formatAmount, showName }) {
  return (
    <>
      <div className="wallet-position-row wallet-lp-line">
        <span className="wallet-position-details">
          <span className="wallet-lp-tag">{lp.kind === 'v2' ? 'LP v2' : 'LP'}</span>
          {showName && lp.name && <>{lp.name} · </>}
          <LpAmounts lp={lp} formatUsd={formatUsd} formatAmount={formatAmount} />
          {lp.feesUsd != null && <span className="wallet-lp-fees"> · fees {formatUsd(lp.feesUsd)}</span>}
        </span>
        {lp.inRange != null && (
          <span className={`wallet-lp-range ${lp.inRange ? 'in' : 'out'}`}
            title={lp.inRange
              ? 'Price inside your range — earning fees'
              : 'Price left your range — NOT earning until it returns or you rebalance'}>
            {lp.inRange ? 'IN' : 'OUT'}
          </span>
        )}
      </div>
      <LpManage lp={lp} />
    </>
  )
}

export default function WalletHoldings({ showToast, dataSource = 'solana', hlPair, data, asPanel = false, open: openProp, setOpen: setOpenProp, closeRequest = 0 }) {
  const { publicKey, signTransaction } = useDualWallet()
  // Header map icon → connected wallet's interaction map popover.
  const [mapOpen, setMapOpen] = useState(false)
  const mapBtnRef = useRef(null)
  const closeMap = useCallback(() => setMapOpen(false), [])

  // ─── Sync quantity with global Order Defaults ───
  const { defaults, setDefaults } = useTradeDefaults()
  const isFutures = dataSource === 'hyperliquid'
  // cmepaper-20261101: CME roots trade PAPER only. Qty = whole contracts.
  const cmeSym = isFutures && cmeInfo(hlPair?.base) ? String(hlPair.base).toUpperCase() : null
  const isCme = !!cmeSym
  const isStk = isFutures && !!stkInfo(hlPair?.base)   // schwab-20261103: view-only
  const [cmeQty, setCmeQtyState] = useState(() => loadCmeQty())
  const [, setCmeTick] = useState(0)
  useEffect(() => {
    const on = () => setCmeTick(t => t + 1)
    window.addEventListener(CME_PAPER_EVENT, on)
    return () => window.removeEventListener(CME_PAPER_EVENT, on)
  }, [])
  const setFutQty = useCallback((v) => {
    setDefaults({ futuresQty: v })
  }, [isCme, setDefaults])
  const cmePxRef = useRef(0)
  const isCmeRef = useRef(false)
  isCmeRef.current = isCme
  const cmePos = isCme ? cmePosition(cmeSym) : null
  // units-20261104: Risk (knob) stays DOLLARS everywhere. The center QTY is a
  // whole-number count where the venue trades units: CME = contracts, Schwab
  // stock = shares, Schwab option = contracts. HL/Solana keep their old meaning.
  const isStkOpt = isStk && !!stkInfo(hlPair?.base)?.opt
  const [stkQty, setStkQtyState] = useState(() => Math.max(1, parseInt(localStorage.getItem('solcheddar:stkQty') || '1', 10) || 1))
  const unitMode = isCme || isStk
  const qtyUnit = isCme || isStkOpt ? 'ct' : isStk ? 'sh' : null
  const displayedQty = isCme ? cmeQty : isStk ? stkQty : isFutures ? defaults.futuresQty : defaults.buyAmountSol
  const setCenterQty = useCallback((v) => {
    if (isCme || isStk) {
      const n = Math.max(1, Math.round(v) || 1)
      if (isCme) { setCmeQtyState(n); saveCmeQty(n) }
      else { setStkQtyState(n); localStorage.setItem('solcheddar:stkQty', String(n)) }
    } else if (isFutures) setFutQty(v)
    else setDefaults({ buyAmountSol: v })
  }, [isCme, isStk, isFutures, setFutQty, setDefaults])

  // ─── Per-section control style (dial | slider), persisted ───
  // Solana and futures sections each remember their own choice. Set via
  // right-click on any control in that section.
  const [solControlStyle, setSolControlStyle] = useState(
    () => localStorage.getItem('solcheddar_sol_control_style') || 'slider'
  )
  const [futuresControlStyle, setFuturesControlStyle] = useState(
    () => localStorage.getItem('solcheddar_futures_control_style') || 'slider'
  )
  useEffect(() => { localStorage.setItem('solcheddar_sol_control_style', solControlStyle) }, [solControlStyle])
  useEffect(() => { localStorage.setItem('solcheddar_futures_control_style', futuresControlStyle) }, [futuresControlStyle])
  const activeControlStyle = isFutures ? futuresControlStyle : solControlStyle
  const setActiveControlStyle = isFutures ? setFuturesControlStyle : setSolControlStyle
  // Right-click menu: {x, y} when open, null when closed.
  const [controlMenu, setControlMenu] = useState(null)
  const controlMenuRef = useRef(null)
  // controls-menu-20261024: it used to close on ANY scroll event anywhere
  // (capture listener on document). The footprint chart's live auto-scroll,
  // the tape's follow-pin and the messages pin all assign scrollTop on data
  // updates, and each of those fires a scroll event — so the menu shut itself
  // within moments on a live screen. Now it closes only on: a pointerdown
  // OUTSIDE it (after a short grace so the opening press/long-press can't
  // close it), Escape, or a scroll of the page itself (document or the phone
  // scroller .workspace-root). Inner element scrolls are ignored.
  useEffect(() => {
    if (!controlMenu) return
    const openedAt = Date.now()
    const close = () => setControlMenu(null)
    const onDown = (e) => {
      if (Date.now() - openedAt < 350) return
      if (controlMenuRef.current && controlMenuRef.current.contains(e.target)) return
      close()
    }
    const onKey = (e) => { if (e.key === 'Escape') close() }
    const onScroll = (e) => {
      const t = e.target
      if (t === document || t === document.documentElement || t === document.body ||
          (t && t.classList && t.classList.contains('workspace-root'))) close()
    }
    window.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey)
    document.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey)
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [controlMenu])
  // Section-level opener: right-click (desktop) or long-press (touch) ANYWHERE
  // in the Controls block, gaps included. Buttons and text fields keep their
  // own behavior (BUY/SELL long-press arms Liq; inputs keep paste/select).
  // Capture-phase handlers, because the knob/slider stop pointerdown
  // propagation. A long-press that opened the menu swallows its trailing click.
  const isNativeCtl = (t) => !!(t && t.closest && t.closest('button, input, textarea, select, a, [contenteditable="true"]'))
  const ctlPressRef = useRef(null)
  const ctlPressFiredRef = useRef(false)
  const cancelCtlPress = () => {
    const s = ctlPressRef.current
    if (s) { clearTimeout(s.t); ctlPressRef.current = null }
  }
  const onControlsContextMenu = (e) => {
    if (isNativeCtl(e.target)) return
    e.preventDefault()
    setControlMenu({ x: e.clientX, y: e.clientY })
  }
  const onControlsPointerDown = (e) => {
    ctlPressFiredRef.current = false
    cancelCtlPress()
    if (e.pointerType === 'mouse' || isNativeCtl(e.target)) return
    const x0 = e.clientX, y0 = e.clientY
    ctlPressRef.current = {
      x0, y0,
      t: setTimeout(() => {
        ctlPressRef.current = null
        ctlPressFiredRef.current = true
        try { navigator.vibrate?.(10) } catch {}
        setControlMenu({ x: x0, y: y0 })
      }, 500),
    }
  }
  const onControlsPointerMove = (e) => {
    const s = ctlPressRef.current
    if (s && (Math.abs(e.clientX - s.x0) > 10 || Math.abs(e.clientY - s.y0) > 10)) cancelCtlPress()
  }
  const onControlsClick = (e) => {
    if (!ctlPressFiredRef.current) return
    ctlPressFiredRef.current = false
    e.preventDefault()
    e.stopPropagation()
  }

  // Floating instance starts OPEN by default at the pinned base position.
  // (Note: if an asPanel grid instance is also mounted, both render — asPanel
  // ignores `open`.)
  // Open/closed is CONTROLLED when App passes it down (header instances), so the
  // footer wallet button can reopen the station after ✕. Uncontrolled otherwise
  // (the asPanel workspace cell, which has no ✕ and never closes) — so this stays
  // a drop-in for every existing mount.
  //
  // The bug this closes: on phone, ✕ set open=false while .wallet-logo — the only
  // toggle that could set it back — is `display: none !important`. The station
  // vanished until reload.
  const [openLocal, setOpenLocal] = useState(true)
  const open = openProp !== undefined ? openProp : openLocal
  const setOpen = setOpenProp || setOpenLocal
  // tif-20261104: Schwab time-in-force, 'day' | 'gtc'. Plain click-to-toggle text under the remote's MKT/LMT.
  const [stkTif, setStkTif] = useState(() => localStorage.getItem('solcheddar:stkTif') === 'gtc' ? 'gtc' : 'day')
  const setTif = (m) => { const v = m === 'limit' ? 'gtc' : 'day'; setStkTif(v); try { localStorage.setItem('solcheddar:stkTif', v) } catch {} }
  const [orderMode, setOrderMode] = useState('market') // 'market' | 'limit' — drives buy/sell order type
  const [holdings, setHoldings] = useState([])
  // ─── Meteora LP positions (read-only stage) ───
  // REST portfolio reads on a 1s poll of the module cache (the cache itself
  // refreshes at 30s TTL) — the SDK stays entirely out of this bundle; see
  // utils/meteoraLp for why and for the action stage's lazy-import plan.
  // Quiet when empty: LP is opportunistic, so the section renders NOTHING
  // unless positions exist — no permanent block to scroll past.
  // Gated on the WALLET only — deliberately not on dataSource. LP positions
  // belong to the wallet, not to whichever chart is open; gating on
  // dataSource meant they were invisible while looking at futures, which is
  // exactly when you'd want to check whether a range went out.
  // EVENT-DRIVEN, not polled: fetch when the panel opens or the wallet
  // changes, re-render only when data actually lands. The 1Hz tick this
  // replaces re-rendered the panel every second and kept an API we don't
  // control on a permanent trickle for data that only moves when you act.
  const [, setLpTick] = useState(0)
  useEffect(() => onLpChange(() => setLpTick(t => t + 1)), [])
  useEffect(() => {
    if (!publicKey || !open) return
    ensureLpPositions(publicKey.toString())
  }, [publicKey, open])
  const lpState = publicKey ? getLpPositions(publicKey.toString()) : null
  // DLMM (REST) and DAMM v2 (RPC) positions arrive merged; either half can
  // fail without hiding the other, so positions render whatever the status.
  const lpPositions = lpState?.positions || []
  const lpByMint = (() => {
    const m = new Map()
    for (const p of lpPositions) {
      if (!p.mintX) continue
      if (!m.has(p.mintX)) m.set(p.mintX, [])
      m.get(p.mintX).push(p)
    }
    return m
  })()
  const lpError = lpErrorText(lpState)
  // Which holding row has its + LP panel expanded (mint string, or null).
  const [lpOpenMint, setLpOpenMint] = useState(null)
  const [loading, setLoading] = useState(false)
  const [closing, setClosing] = useState(null)
  const menuRef = useRef(null)
  const buttonRef = useRef(null)

  // ─── Draggable / Pin state (like BotSettingsMenu) ───
  // The panel REMEMBERS where it lives. Closing no longer throws the position
  // away (finishClose used to reset pinned+dragOffset, which is why a reopen
  // always snapped back to the header button instead of coming home), and the
  // position survives a reload for the length of the tab session.
  //
  // sessionStorage, not localStorage: "during a session" — a fresh tab gets the
  // default (10, 175) rather than inheriting where you happened to leave it a
  // week ago. Only the floating instances persist; the asPanel grid cell has no
  // position of its own and would just clobber the key with the default.
  const [pinned, setPinned] = useState(true)
  const [dragOffset, setDragOffset] = useState(readStationOffset)
  useEffect(() => {
    if (asPanel) return   // the grid cell has no position of its own to save
    try { sessionStorage.setItem(STATION_POS_KEY, JSON.stringify(dragOffset)) } catch {}
  }, [asPanel, dragOffset])
  const [buttonPos, setButtonPos] = useState({ x: 0, y: 0 })
  const dragRef = useRef({ active: false, startX: 0, startY: 0, startOffX: 0, startOffY: 0 })

  // ─── Hold position (holdings-hold-20261024) ───
  // Unchecked: today's panel — position:fixed, floats above the scroll.
  // Checked: the SAME free, draggable panel, but portaled into the phone's
  // scroller (.workspace-root) as position:absolute in its CONTENT coords, so
  // it scrolls away with the app. No reserved space. Desktop doesn't scroll,
  // so it stays fixed there (looks identical either way); the pref still saves.
  // Coords: dragOffset stays viewport-space (base 10/175, sessionStorage
  // STATION_POS_KEY); heldPos is scroller-content-space {x,y} in its own key.
  // Everything referenced here (asPanel, open, menuRef, dragOffset) is above.
  const holdEligible = !asPanel && isPhoneDevice()
  const [holdPref, setHoldPref] = useState(() => {
    try { return localStorage.getItem('solcheddar_station_hold') === '1' } catch { return false }
  })
  const [heldPos, setHeldPos] = useState(() => {
    try {
      const v = JSON.parse(sessionStorage.getItem('solcheddar_station_held_pos'))
      if (v && typeof v.x === 'number' && typeof v.y === 'number') return v
    } catch {}
    return null
  })
  useEffect(() => {
    if (!heldPos) return
    try { sessionStorage.setItem('solcheddar_station_held_pos', JSON.stringify(heldPos)) } catch {}
  }, [heldPos])
  const [holdHost, setHoldHost] = useState(null)
  useEffect(() => {
    setHoldHost(holdPref && holdEligible ? document.querySelector('.workspace-root') : null)
  }, [holdPref, holdEligible, open])
  // viewport (client) px → the scroller's content px
  const toHostCoords = (host, vx, vy) => {
    const r = host.getBoundingClientRect()
    return { x: vx - r.left - host.clientLeft + host.scrollLeft, y: vy - r.top - host.clientTop + host.scrollTop }
  }
  // Held with no saved held spot (fresh tab): anchor where the floating one sits.
  useEffect(() => {
    if (!holdHost || heldPos) return
    setHeldPos(toHostCoords(holdHost, 10 + dragOffset.x, 175 + dragOffset.y))
  }, [holdHost, heldPos, dragOffset])
  const held = !!(holdPref && holdEligible && heldPos && holdHost && holdHost.isConnected)
  // Toggle converts from the panel's CURRENT on-screen rect so it doesn't jump.
  const toggleHold = () => {
    const next = !holdPref
    try { localStorage.setItem('solcheddar_station_hold', next ? '1' : '0') } catch {}
    const r = menuRef.current?.getBoundingClientRect?.()
    if (holdEligible && r && r.width > 0) {
      if (next) {
        const host = document.querySelector('.workspace-root')
        if (host) setHeldPos(toHostCoords(host, r.left, r.top))
      } else {
        setPinned(true)
        setDragOffset({ x: r.left - 10, y: r.top - 175 })
      }
    }
    setHoldPref(next)
  }

  // ─── Keep the floating station on screen ───
  // Portrait⇄landscape (or any resize) can leave the saved position outside
  // the new viewport and the panel is lost. After the layout settles, nudge
  // it back inside — ONLY if it's actually off; a panel that fits is left
  // exactly where it is. Uses offsetWidth/Height + the inline left/top, not
  // getBoundingClientRect, so the open/close animation transform can't skew it.
  useEffect(() => {
    if (asPanel || !open) return
    const fit = () => {
      if (dragRef.current.active) return
      const el = menuRef.current
      if (!el) return
      const curL = parseFloat(el.style.left), curT = parseFloat(el.style.top)
      if (!Number.isFinite(curL) || !Number.isFinite(curT)) return
      const w = el.offsetWidth, h = el.offsetHeight
      if (held) {
        // Content space: keep it across the scroller's width; vertically just
        // not above the top (scrolling reaches the rest).
        const cw = holdHost.clientWidth, M = 8
        const left = Math.max(M, Math.min(curL, cw - w - M))
        const top = Math.max(M, curT)
        if (Math.abs(left - curL) < 1 && Math.abs(top - curT) < 1) return
        setHeldPos({ x: left, y: top })
        return
      }
      const vw = window.innerWidth, vh = window.innerHeight, M = 8
      const left = Math.max(M, Math.min(curL, vw - w - M))
      const top  = Math.max(M, Math.min(curT, Math.max(M, vh - h - M)))
      if (Math.abs(left - curL) < 1 && Math.abs(top - curT) < 1) return
      setPinned(true)
      setDragOffset({ x: left - 10, y: top - 175 })
    }
    // Rotation reports the new size late on some phones: check twice.
    let t1, t2
    const onChange = () => {
      clearTimeout(t1); clearTimeout(t2)
      t1 = setTimeout(fit, 60); t2 = setTimeout(fit, 450)
    }
    onChange()   // also on open: a restored session position may be off-screen
    window.addEventListener('resize', onChange)
    window.addEventListener('orientationchange', onChange)
    return () => {
      clearTimeout(t1); clearTimeout(t2)
      window.removeEventListener('resize', onChange)
      window.removeEventListener('orientationchange', onChange)
    }
  }, [asPanel, open, held, holdHost])
  // Touch-drag guard: set while a finger gesture owns the titlebar so the
  // synthetic mouse events a tap replays don't also fire handleDragStart.
  const touchActiveRef = useRef(false)

  const [isBuying, setIsBuying] = useState(false)
  const [isSelling, setIsSelling] = useState(false)

  // ─── Liq arm — one-shot buy/sell on the next Liq ≤3s reaction ───
  // null   → off (BUY/SELL execute immediately, as always)
  // 'armed'→ bot icon lit: the NEXT click on BUY or SELL queues instead of
  //          executing
  // 'buy'  → BUY queued: fires trading's market buy on the next Liq ≤3s UP
  //          reaction (swept low reclaimed)
  // 'sell' → SELL queued: fires the market sell on the next Liq ≤3s DOWN
  //          reaction (swept high reclaimed)
  // (longpress-liq-20261017: was the next ARD triangle. The ard* names stay
  // to keep the diff small; the trigger is Liq.)
  // The chart's OrderflowBot owns the liq tracker; the chart re-broadcasts
  // each completed reclaim as 'solcheddar-liq-reaction'.
  const [ardArm, setArdArm] = useState(null)
  const ardArmRef = useRef(null)
  // When the current arm was set (client clock). Only reactions that
  // complete AFTER this count, so history replays can't fire it.
  const ardArmAtRef = useRef(0)
  useEffect(() => {
    ardArmRef.current = ardArm
    ardArmAtRef.current = ardArm ? Date.now() : 0
  }, [ardArm])

  // ─── Collapsed mode: double-click titlebar → show only header + buy/sell ───
  // Phone opens COLLAPSED — just the titlebar and the buy/sell row, which is the
  // 90% case: you want to fire a trade, not read positions. Double-tap the
  // titlebar to expand (that machinery already existed; only the default moved).
  // Desktop keeps opening expanded — there's room for it there.
  // isPhoneViewport(), not useIsPhone(): this is an initial DEFAULT, not a live
  // flag. A live one would re-collapse the panel the moment you rotated the
  // phone — mid-trade, without asking. The snapshot is the correct semantic and
  // it was already what this did; now it just doesn't own a copy of 768.
  // Phone no longer collapses at all (spec): panel is always fully open,
  // buy/sell relocated under the header instead. Desktop keeps its
  // double-click collapse. Init is therefore always expanded.
  const [collapsed, setCollapsed] = useState(false)

  // ─── Close choreography ───
  // ✕ doesn't just unmount: it collapses, then creases shut along a diagonal.
  // The two stages OVERLAP (the fold starts 120ms into the 220ms collapse, see
  // the keyframes) — sequenced they ran 460ms, which is long enough to feel like
  // friction by the tenth close. Overlapped it's ~320ms and reads as one motion.
  // `panelClosing`, not `closing` — `closing` is already taken above for the
  // per-position close spinner. Different thing entirely.
  const [panelClosing, setPanelClosing] = useState(false)
  const [panelOpening, setPanelOpening] = useState(false)
  // Armed by finishClose. Without it the panel would play the unfold on FIRST
  // app load too — it mounts with open=true, which looks identical to a reopen
  // from this side. It shouldn't animate its way onto a cold screen.
  const justClosedRef = useRef(false)
  const openTimerRef = useRef(null)
  const ghostTimerRef = useRef(null)
  // Viewport rect of the panel, snapped at close-start. The ghost outlines have
  // to render OUTSIDE .wallet-toolbar — that's the element carrying the
  // clip-path, and clip-path clips every descendant (it even becomes the
  // containing block for position:fixed ones), so a ghost inside would get
  // folded away along with the piece it exists to commemorate. .wallet-toolbar
  // is position:fixed, so a fixed overlay at its measured rect lands exactly on
  // top of it, in the same coordinate space, outside the clip.
  const [ghostRect, setGhostRect] = useState(null)
  const preCloseCollapsed = useRef(false)
  const closeTimerRef = useRef(null)

  const finishClose = useCallback(() => {
    clearTimeout(closeTimerRef.current)
    clearTimeout(ghostTimerRef.current)
    setPanelClosing(false)
    setGhostRect(null)
    setOpen(false)
    justClosedRef.current = true   // arm the reverse-unfold for the next open
    // DELIBERATELY not touched here:
    //   • pinned / dragOffset — this used to reset them, which is exactly why a
    //     reopen snapped back to the header button instead of coming home. The
    //     position IS the panel's memory of where it lives. Closing shouldn't
    //     erase it any more than shutting a drawer should move the desk.
    //   • collapsed — left collapsed, on purpose. The close folded a COLLAPSED
    //     strip, so a true reverse has to unfold back into one and expand after.
    //     preCloseCollapsed is restored at the END of the open sequence instead.
  }, [setOpen])

  const startClose = useCallback(() => {
    if (panelClosing) return   // double-click guard: don't restart mid-fold
    // Reduced motion: no choreography. Handled HERE and not with a CSS media
    // query, because killing the animation in CSS would mean animationend never
    // fires and the panel would sit there closing forever.
    if (typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) {
      // No choreography — but the position still survives, and `collapsed` is
      // left exactly as it is (nothing collapsed it, so nothing has to undo it).
      setOpen(false)
      return
    }
    preCloseCollapsed.current = collapsed
    setCollapsed(true)     // stage 1 — collapse
    setPanelClosing(true)  // stage 2 — the fold (CSS-delayed by FOLD_DELAY_MS)

    // Measure the ghost box AT FOLD-START, never at close-start.
    //
    // This was the bug. Measuring here-and-now captures the EXPANDED panel — and
    // then the collapse runs and the panel becomes a strip, but the ghosts are
    // still pinned to that stale full-height rect. So you'd watch nine bright
    // outlines fold a panel that wasn't there any more. It looked correct when
    // the panel was ALREADY collapsed, because then nothing shrank and the stale
    // measurement happened to be right.
    //
    // The clip-path never had this problem — its percentages resolve against the
    // live border box, so it always folded the real panel. Only the ghosts, which
    // live in a fixed overlay outside the panel, needed telling.
    //
    // A timer rather than a transitionend listener on .wallet-collapsible: if the
    // panel is already collapsed, setCollapsed(true) is a no-op, no transition
    // fires, and a listener would wait forever. FOLD_DELAY_MS (150) clears
    // COLLAPSE_MS (140), so by now the box has stopped moving either way.
    clearTimeout(ghostTimerRef.current)
    ghostTimerRef.current = setTimeout(() => {
      const r = menuRef.current?.getBoundingClientRect?.()
      if (r && r.width > 0) setGhostRect({ left: r.left, top: r.top, width: r.width, height: r.height })
    }, FOLD_DELAY_MS)
    // Belt. If animationend never lands (tab backgrounded mid-fold, animation
    // dropped, element hidden) the panel would be stranded: still mounted, still
    // `closing`, pointer-events:none. A dead box you can't click out of.
    clearTimeout(closeTimerRef.current)
    closeTimerRef.current = setTimeout(finishClose, CLOSE_TOTAL_MS + 240)
  }, [panelClosing, collapsed, setOpen, finishClose])

  // ─── Open: the close, played backwards ───
  // Unfold (the fold in reverse), THEN expand. That ordering is what makes it a
  // real reverse rather than a different animation that happens to run the other
  // way: the close collapsed and then folded a strip, so the open has to unfold
  // back into a strip and expand out of it.
  //
  // useLayoutEffect, not useEffect. The panel's first render after reopening has
  // no .wallet-opening class on it yet — it would paint fully visible for one
  // frame and THEN snap to folded. A layout effect runs before paint, so the
  // class lands on the very first frame the user sees.
  useLayoutEffect(() => {
    if (!open || asPanel) return
    if (!justClosedRef.current) return   // cold mount, not a reopen — no animation
    justClosedRef.current = false
    // menuRef is populated by now (layout effect = after render), and `collapsed`
    // is still true, so this is the strip's rect — the same box the fold left.
    const r = menuRef.current?.getBoundingClientRect?.()
    if (r && r.width > 0) setGhostRect({ left: r.left, top: r.top, width: r.width, height: r.height })
    setPanelOpening(true)
    clearTimeout(openTimerRef.current)
    openTimerRef.current = setTimeout(() => {
      setPanelOpening(false)
      setGhostRect(null)
      setCollapsed(preCloseCollapsed.current)   // expand LAST — the final beat
    }, FOLD_DUR_MS + 40)
  }, [open, asPanel])

  // ─── Close requested from outside (the footer wallet button) ───
  // App can't call startClose() — it lives in here, and the footer button is a
  // sibling of this panel, not its parent. So App bumps a counter and we turn
  // that intent into the SAME close the ✕ runs. Before this, closing from the
  // footer flipped `open` straight to false: no fold, and no justClosedRef, so
  // the reopen skipped the unfold too. Three close paths, one animation.
  //
  // A counter rather than a boolean, so two closes in a row are two distinct
  // events and not one edge that never re-fires.
  const lastCloseReqRef = useRef(closeRequest)
  useEffect(() => {
    if (closeRequest === lastCloseReqRef.current) return
    lastCloseReqRef.current = closeRequest
    if (open && !asPanel) startClose()
  }, [closeRequest, open, asPanel, startClose])

  useEffect(() => () => {
    clearTimeout(closeTimerRef.current)
    clearTimeout(openTimerRef.current)
    clearTimeout(ghostTimerRef.current)
  }, [])
  // Never come back mid-fold: if `open` is flipped true from outside (the footer
  // button) while a close is in flight, clear the closing flag or the panel would
  // remount already wearing .wallet-closing and immediately fold again.
  useEffect(() => {
    if (open) { clearTimeout(closeTimerRef.current); clearTimeout(ghostTimerRef.current); setPanelClosing(false) }
  }, [open])

  // ─── Futures state ───
  const [hlPositions, setHlPositions] = useState([])
  const [hlOrders, setHlOrders] = useState([])
  const [cancellingId, setCancellingId] = useState(null)
  const [closingPos, setClosingPos] = useState(null)
  // Available USDC margin from HL account poll (broadcast by useTrading)
  const [hlAvailable, setHlAvailable] = useState(0)

  // ─── Wallet balance ───
  const [walletBalance, setWalletBalance] = useState(null)

  // ─── Bot microstructure metrics ───
  const [botMetrics, setBotMetrics] = useState({ vacuumScore: 0, densityRatio: 0, approachVelocity: 0, churnScore: 0 })

  // ─── Churn gauge alert: low rumble on each segment increase ───
  const vdFilledRef = useRef(0)
  useEffect(() => {
      const val = botMetrics.churnScore
      const filled = Math.round((Math.min(val, 10) / 10) * 7)
      if (filled > vdFilledRef.current) {
        try {
          const ac = new (window.AudioContext || window.webkitAudioContext)()
          const t0 = ac.currentTime
          // three short low pulses — "br-b-b"
          ;[0, 0.045, 0.18].forEach((offset) => {
            const osc = ac.createOscillator()
            const gain = ac.createGain()
            osc.connect(gain); gain.connect(ac.destination)
            osc.type = 'square'
            osc.frequency.value = 1300
            gain.gain.setValueAtTime(0.0001, t0 + offset)
            gain.gain.exponentialRampToValueAtTime(0.3, t0 + offset + 0.005)
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + offset + 0.06)
            osc.start(t0 + offset)
            osc.stop(t0 + offset + 0.07)
          })
          // close the context after the last pulse finishes
          setTimeout(() => ac.close(), 300)
        } catch {}
      }
      vdFilledRef.current = filled
    }, [botMetrics.churnScore])

  // ─── Track current token from FootprintChart ───
  const [currentToken, setCurrentToken] = useState(null)

  // ─── Shared trading context (single instance with chart) ───
  const trading = useTradingContext()
  // push-order-20261021: armed order type + settings (drawer at the bottom).
  // Default 'liq' with 3s keeps the old long-press BUY exactly as it was.
  const [orderEntry, updateOrderEntry] = useOrderEntry()
  const liqFireMaxSec = orderEntry.liqSecs
  const liqArmLabel = `Liquidity`
  // order-chain-20261021: hooks report each run's end here (ok = completed).
  const chainRef = useRef(null)          // { ids, i, liqArmed, liqFired } while a chain runs
  const chainEndRef = useRef(null)
  const onOrderEnd = (ok) => { try { chainEndRef.current?.(ok) } catch {} }
  const pushOrder = usePushOrder({ cfg: orderEntry, trading, currentToken, publicKey, buyAmountSol: defaults.buyAmountSol, sellPercentage: defaults.sellPercentage, isFutures, showToast, onEnd: onOrderEnd })
  // grok-orders-20261021: Grok recipes join the picker list (tiny G tag);
  // only the user arms them, one at a time. Buys capped by Push's max buy.
  const recipeOrder = useRecipeOrder({ trading, currentToken, publicKey, buyAmountSol: defaults.buyAmountSol, maxSol: orderEntry.push.maxSol, pushOrder, isFutures, showToast, onEnd: onOrderEnd })
  const orderHooksRef = useRef({})
  orderHooksRef.current = { push: pushOrder, recipe: recipeOrder }
  const [recipes, setRecipes] = useState(() => { try { return listRecipes() } catch { return [] } })
  useEffect(() => {
    const h = () => { try { setRecipes(listRecipes()) } catch {} }
    window.addEventListener('solcheddar-order-recipes', h)
    return () => window.removeEventListener('solcheddar-order-recipes', h)
  }, [])
  useEffect(() => { try { setMyWallet(publicKey ? publicKey.toString() : null) } catch {} }, [publicKey])
  const pickerOrders = [...ORDERS, ...recipes.map(rc => ({
    id: `r:${rc.id}`, label: rc.label, desc: rc.desc, by: 'grok',
    settings: (rc.settings || []).slice(0, 1).map(st => ({
      label: st.label, min: st.min, max: st.max, step: st.step,
      get: () => st.value, set: (v) => { updateRecipeSetting(rc.id, st.key, v); return null }, chip: (v) => `${v}`,
    })),
  }))]
  const chainIds = orderEntry.chain.filter(id => pickerOrders.some(o => o.id === id))   // drops deleted recipes
  const chainIdsRef = useRef(chainIds)
  chainIdsRef.current = chainIds
  const onDeleteOrder = (oid) => {
    try {
      const res = deleteRecipe(String(oid).replace(/^r:/, ''))
      if (res.ok) { updateOrderEntry({ ...(orderEntry.type === oid ? { type: 'liq' } : {}), chain: orderEntry.chain.filter(x => x !== oid) }); showToast?.('Order deleted', 'sell') }
      else showToast?.(res.error, 'sell')
      closePicker()
    } catch {}
  }
  const onAskOrder = (text) => {
    try {
      window.dispatchEvent(new CustomEvent('solcheddar-grok-ask', { detail: { text:
        `[order recipe request] Create an order recipe and save it with app_action save_order_recipe (never arm it). User wants: ${text}` } }))
      showToast?.('Sent to Grok chat', 'buy')
      closePicker()
    } catch {}
  }

  useEffect(() => {
    const handler = (e) => {
      if (e.detail?.token) setCurrentToken(e.detail.token)
    }
    window.addEventListener('solcheddar-token-changed', handler)
    return () => window.removeEventListener('solcheddar-token-changed', handler)
  }, [])

  // ─── Bot microstructure metrics listener ───
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setBotMetrics(e.detail)
    }
    window.addEventListener('solcheddar-bot-metrics', handler)
    return () => window.removeEventListener('solcheddar-bot-metrics', handler)
  }, [])

  // ─── Fetch wallet balance ───
  const balanceFetchRef = useRef(null)
  useEffect(() => {
    if (!publicKey || isFutures) {
      setWalletBalance(null)
      return
    }
    const fetchBalance = () => {
      // A failed read keeps the last good balance (was: blank it).
      getSOLBalance(publicKey).then(setWalletBalance).catch(() => {})
    }
    balanceFetchRef.current = fetchBalance
    fetchBalance()
    const interval = setInterval(fetchBalance, 30000)
    return () => { clearInterval(interval); balanceFetchRef.current = null }
  }, [publicKey, isFutures])

  // ─── Drag handlers (like BotSettingsMenu) ───
  // Perf: during a drag we do NOT call setState per mousemove — that would
  // re-render this whole (heavy) component 60-120×/sec. Instead we write
  // left/top straight to the DOM node via requestAnimationFrame, and commit
  // to React state exactly once on mouseup. The render also reads the live
  // ref position while a drag is active (see panelX/panelY) so the frequent
  // liveMcap-driven re-renders don't snap the panel back to its old spot.
  const handleDragStart = (e) => {
    if (touchActiveRef.current) return   // a touch gesture owns this drag
    if (e.button !== 0) return
    e.preventDefault()

    // ONE coordinate base everywhere: the pinned render is
    // left = 10 + dragOffset.x, top = 175 + dragOffset.y — so pin
    // conversion, live-drag math, and the mouseup commit all use
    // (10, 175). The old code used three different bases (10/175
    // render, 20/200 drag, 30/200 pin), so the panel jumped once on
    // grab, once on first move, and again on release.
    // If not already pinned, pin it at EXACTLY its current position.
    if (!pinned) {
      setPinned(true)
      setDragOffset({ x: buttonPos.x - 10, y: buttonPos.y - 175 })
    }

    const d = dragRef.current
    d.active = true
    d.startX = e.clientX
    d.startY = e.clientY
    d.startOffX = held ? heldPos.x - 10 : pinned ? dragOffset.x : buttonPos.x - 10
    d.startOffY = held ? heldPos.y - 175 : pinned ? dragOffset.y : buttonPos.y - 175
    const wasHeld = held   // drag deltas are the same px in either space
    // Absolute live position (px) we mutate during the drag
    d.liveLeft = 10 + d.startOffX
    d.liveTop = 175 + d.startOffY
    d.raf = 0

    const onMove = (ev) => {
      if (!d.active) return
      const dx = ev.clientX - d.startX
      const dy = ev.clientY - d.startY
      d.liveLeft = 10 + d.startOffX + dx
      d.liveTop = 175 + d.startOffY + dy
      // Batch DOM writes to one per frame; no React re-render
      if (!d.raf) {
        d.raf = requestAnimationFrame(() => {
          d.raf = 0
          const el = menuRef.current
          if (el) {
            el.style.left = d.liveLeft + 'px'
            el.style.top = d.liveTop + 'px'
          }
        })
      }
    }
    const onUp = () => {
      d.active = false
      if (d.raf) { cancelAnimationFrame(d.raf); d.raf = 0 }
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      // Commit final position to state ONCE so the model stays in sync
      // (same 10/175 base the render uses — no snap on release)
      if (wasHeld) setHeldPos({ x: d.liveLeft, y: d.liveTop })
      else setDragOffset({ x: d.liveLeft - 10, y: d.liveTop - 175 })
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // ─── Long-press touch drag (phone) ───
  // The mouse path above is button-gated and binds mouse listeners, so touch
  // never reaches it. Here a ~400ms long-press on the titlebar picks the
  // panel up (haptic on supporting devices), then touchmove repositions it
  // with the SAME rAF DOM-write + single state commit. A finger move before
  // the timer fires is treated as a scroll and cancels the press. Model
  // state is snapshotted at press-start, mirroring the mouse path which
  // reads it synchronously on mousedown.
  const handleTitleTouchStart = (e) => {
    if (e.touches.length !== 1) return
    const t0 = e.touches[0]
    const startX = t0.clientX
    const startY = t0.clientY
    const wasPinned  = pinned
    const wasHeld    = held
    const baseOffX   = held ? heldPos.x - 10 : pinned ? dragOffset.x : buttonPos.x - 10
    const baseOffY   = held ? heldPos.y - 175 : pinned ? dragOffset.y : buttonPos.y - 175
    const pinFallback = { x: buttonPos.x - 10, y: buttonPos.y - 175 }
    touchActiveRef.current = true

    const d = dragRef.current
    let pressTimer = setTimeout(() => {
      pressTimer = null
      if (!wasPinned && !wasHeld) { setPinned(true); setDragOffset(pinFallback) }
      d.active   = true
      d.startX   = startX
      d.startY   = startY
      d.startOffX = baseOffX
      d.startOffY = baseOffY
      d.liveLeft = 10 + baseOffX
      d.liveTop = 175 + baseOffY
      d.raf = 0
      try { navigator.vibrate?.(15) } catch {}
    }, 400)

    const cleanup = () => {
      window.removeEventListener('touchmove', onMove)
      window.removeEventListener('touchend', onEnd)
      window.removeEventListener('touchcancel', onEnd)
    }
    const onMove = (ev) => {
      const t = ev.touches[0]
      if (!t) return
      if (!d.active) {
        // Pre-arm movement = the user is scrolling, not dragging → abort.
        if (pressTimer && (Math.abs(t.clientX - startX) > 10 || Math.abs(t.clientY - startY) > 10)) {
          clearTimeout(pressTimer); pressTimer = null
          cleanup()
        }
        return
      }
      ev.preventDefault()   // armed: take over the gesture, block page scroll
      d.liveLeft = 10 + baseOffX + (t.clientX - d.startX)
      d.liveTop = 175 + baseOffY + (t.clientY - d.startY)
      if (!d.raf) {
        d.raf = requestAnimationFrame(() => {
          d.raf = 0
          const el = menuRef.current
          if (el) { el.style.left = d.liveLeft + 'px'; el.style.top = d.liveTop + 'px' }
        })
      }
    }
    const onEnd = () => {
      if (pressTimer) { clearTimeout(pressTimer); pressTimer = null }
      if (d.active) {
        d.active = false
        if (d.raf) { cancelAnimationFrame(d.raf); d.raf = 0 }
        if (wasHeld) setHeldPos({ x: d.liveLeft, y: d.liveTop })
        else setDragOffset({ x: d.liveLeft - 10, y: d.liveTop - 175 })
      }
      cleanup()
      // Hold the guard briefly so the trailing synthetic mousedown
      // (fired ~300ms after touchend) can't re-trigger handleDragStart.
      setTimeout(() => { touchActiveRef.current = false }, 500)
    }
    window.addEventListener('touchmove', onMove, { passive: false })
    window.addEventListener('touchend', onEnd)
    window.addEventListener('touchcancel', onEnd)
  }

  // ─── Click outside to close (only if not pinned) ───
  useEffect(() => {
    if (!open || asPanel) return
    const handler = (e) => {
      if (pinned) return
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false)
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open, pinned, asPanel])

  // Reset drag offset when opening fresh (unless pinned)
  useEffect(() => {
    if (open && !pinned) {
      setDragOffset({ x: 0, y: 0 })
      // Also refresh button position
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setButtonPos({ x: rect.left, y: rect.bottom + 5 })
      }
    }
  }, [open, pinned])

  // Note: buy event listener is defined after fetchHoldings to avoid initialization order issues

  // ─── Quantity scroll handler ───
  const handleQuantityWheel = useCallback((e) => {
    e.preventDefault()
    const delta = e.deltaY > 0 ? -1 : 1
    const step = unitMode ? 1 : isFutures ? 10 : 0.05
    const newVal = displayedQty + (delta * step)
    const clamped = unitMode ? newVal
      : isFutures ? Math.max(0.0001, Math.round(newVal * 1000) / 1000)
      : Math.max(0.001, Math.round(newVal * 1000) / 1000)
    setCenterQty(clamped)
  }, [displayedQty, isFutures, unitMode, setCenterQty])

  // ─── Order execution ───
  // cmepaper-20261101: paper fill at the chart's last trade. Opposite side
  // closes (FLAT at 100%, REDUCE by sell% otherwise); same side/flat adds qty.
  const cmeOrder = useCallback((side) => {
    try {
      const px = cmePxRef.current
      const p = cmePosition(cmeSym)
      const opp = p && ((side === 'buy') !== (p.side === 'LONG'))
      let r
      if (opp && defaults.sellPercentage >= 100) r = cmeFlatten(cmeSym, px)
      else if (opp) r = cmeFill(cmeSym, side, Math.max(1, Math.ceil(p.qty * defaults.sellPercentage / 100)), px)
      else {
        // units-20261104: contracts straight from the center QTY.
        const ct = Math.max(1, Math.floor(cmeQty || 1))
        if (!cmeCanAfford(cmeSym, side, ct, px)) {
          const a = cmeAccount(cmeSym, px).available
          return showToast?.(`PAPER: ${ct} ct needs $${(ct * CME_MARGIN_PER_CT).toLocaleString()} margin, $${Math.max(0, a).toFixed(0)} available`, 'sell')
        }
        r = cmeFill(cmeSym, side, ct, px)
      }
      const rz = r.realized ? ` · ${r.realized >= 0 ? '+' : '-'}$${Math.abs(r.realized).toFixed(2)}` : ''
      showToast?.(`PAPER ${side === 'buy' ? 'BUY' : 'SELL'} ${cmeSym} @ ${px}${rz}`, side)
    } catch (err) { showToast?.(`Paper order failed: ${err.message}`, 'sell') }
  }, [cmeSym, cmeQty, defaults.sellPercentage, showToast])

  // schwabtrade-20261104: Schwab stock orders through the owner-locked droplet
  // route -> Corsair relay. QTY = shares, MKT/LMT follows the remote's toggle,
  // LMT price = last. Account = the badge's selection. Confirm before send.
  const stkOrder = useCallback(async (side) => {
    const si = stkInfo(hlPair?.base)
    const sym = si?.sym?.toUpperCase()
    const owner = localStorage.getItem(SCHWAB_OWNER) || ''
    const idx = parseInt(localStorage.getItem(SCHWAB_ACCT) || '0', 10) || 0
    const qty = Math.floor(Number(stkQty) || 0)
    const px = Math.round((cmePxRef.current || 0) * 100) / 100
    const S = side.toUpperCase(), lmt = orderMode === 'limit'
    if (si?.opt) return showToast?.('Schwab: options trading not enabled', 'sell')
    if (!owner) return showToast?.('Connect Schwab first', 'sell')
    if (!sym || qty < 1 || !px) return showToast?.('Schwab: need QTY (shares) and a live price', 'sell')
    const cap = Number(defaults.futuresQty) || 0
    if (side === 'buy' && cap > 0 && qty * px > cap) return showToast?.(`Schwab: ${qty} sh ~$${(qty * px).toFixed(0)} exceeds Risk $${cap.toFixed(0)}`, 'sell')
    if (!window.confirm(`Schwab ${S} ${qty} ${sym} ${lmt ? 'LIMIT @ $' + px.toFixed(2) : 'MARKET (last $' + px.toFixed(2) + ')'}\n~$${(qty * px).toFixed(0)} | account #${idx + 1} | ${stkTif === 'gtc' ? 'GTC' : 'DAY'}\n\nSend order?`)) return
    const hdr = { 'X-Owner-Key': owner, 'Content-Type': 'application/json' }
    const r = await fetch(`${SCHWAB_BASE}/stk/order`, { method: 'POST', headers: hdr,
      body: JSON.stringify({ idx, symbol: sym, side: S, qty, type: lmt ? 'LIMIT' : 'MARKET', price: lmt ? px : 0, ref: px, tif: stkTif === 'gtc' ? 'GTC' : 'DAY' }) })
    const d = await r.json().catch(() => ({}))
    if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
    showToast?.(`Schwab ${S} ${qty} ${sym} sent${d.id ? ' #' + d.id : ''}`, side)
    setTimeout(async () => {   // status/fill readback
      try {
        const o = await (await fetch(`${SCHWAB_BASE}/stk/orders?idx=${idx}`, { headers: hdr })).json()
        const m = Array.isArray(o) && (o.find(x => String(x.id) === String(d.id)) || o[0])
        if (m) showToast?.(`Schwab #${m.id} ${m.status} ${m.filled || 0}/${m.qty}${m.price ? ' @ ' + m.price : ''}`, side)
      } catch { /* readback is best-effort */ }
    }, 3000)
  }, [hlPair?.base, stkQty, orderMode, stkTif, showToast, defaults.futuresQty])
  const stkTry = (side) => stkOrder(side).catch(e => showToast?.(`Schwab order failed: ${e.message}`, 'sell'))

  const handleBuy = useCallback(async () => {
    if (isStk) return stkTry('buy')
    if (isCme) return cmeOrder('buy')
    if (!isFutures && !currentToken) return showToast?.('Select a token first', 'sell')
    // Armed-chart authority: executeMarketOrder trades the CONTEXT's charted
    // token no matter what this panel believes. This panel's copy is fed by a
    // window event — if a switch's broadcast was missed, the two diverge
    // silently and clicks look wrong/dead. Detect, adopt the context token,
    // and SAY so, so drift episodes become visible instead of mysterious.
    if (!isFutures && trading.currentToken && currentToken !== trading.currentToken) {
      showToast?.('Holdings had a stale token — synced to charted', 'buy')
      setCurrentToken(trading.currentToken)
    }
    setIsBuying(true)
    try {
      if (orderMode === 'limit') {
        // LMT mode: place a limit at current price (placeLimitDirect defaults
        // the price internally). The order tag is the feedback — no instant
        // position tag, nothing has filled yet.
        await trading.placeLimitDirect('buy')
      } else {
        await trading.executeMarketOrder('buy')
        // Instant position tag — tell chart to create position at current mcap NOW
        // Tape detection will average in the exact entry via addPosition if it catches it
        // paperclose-20261027: not for paper buys (no chain fill; the chip shows those).
        if (!isFutures && !isPaperActive()) {
          window.dispatchEvent(new CustomEvent('solcheddar-create-position', {
            detail: { token: currentToken, solAmount: defaults.buyAmountSol }
          }))
        }
      }
    } catch (err) {
      showToast?.(`Buy failed: ${err.message}`, 'sell')
    }
    setIsBuying(false)
  }, [currentToken, trading, showToast, isFutures, defaults.buyAmountSol, orderMode, isCme, isStk, cmeOrder, stkOrder])

  const handleSell = useCallback(async () => {
    if (isStk) return stkTry('sell')
    if (isCme) return cmeOrder('sell')
    if (!isFutures && !currentToken) return showToast?.('Select a token first', 'sell')
    if (!isFutures && trading.currentToken && currentToken !== trading.currentToken) {
      showToast?.('Holdings had a stale token — synced to charted', 'sell')
      setCurrentToken(trading.currentToken)
    }
    setIsSelling(true)
    try {
      if (orderMode === 'limit') {
        // LMT mode: place a sell-limit at current price. Nothing has filled,
        // so no position-closed / sell-executed events — those fire on fills,
        // and firing them here would clear tags for an order still resting.
        await trading.placeLimitDirect('sell')
      } else {
        await trading.executeMarketOrder('sell')

        // Only clear position tag on full sells — partials are handled inside executeMarketOrder
        if (!isFutures && defaults.sellPercentage >= 100) {
          window.dispatchEvent(new CustomEvent('solcheddar-position-closed', {
            detail: { mint: currentToken }
          }))
        }

        // Dispatch event to trigger holdings refresh after sell settles
        if (!isFutures) {
          window.dispatchEvent(new CustomEvent('solcheddar-sell-executed', {
            detail: { token: currentToken }
          }))
        }
      }
    } catch (err) {
      showToast?.(`Sell failed: ${err.message}`, 'sell')
    }
    setIsSelling(false)
  }, [currentToken, trading, showToast, isFutures, defaults.sellPercentage, orderMode, isCme, isStk, cmeOrder, stkOrder])

  // ─── Liq arm: reaction listener + safety disarms ───
  // (The old 'solcheddar-ard-arm' broadcast is gone: the bot's liq tracker
  // runs on every chart tick, bot on or off, so nothing needs waking.)
  //
  // The trigger: a fresh Liq reaction from the chart's bot. BUY fires on an
  // UP reaction, SELL on a DOWN one, only if it took ≤ liqFireMaxSec and
  // completed after the arm was set. Token-guarded so a second chart slot
  // can't fire this panel's queue. One-shot: disarm BEFORE executing.
  useEffect(() => {
    if (ardArm !== 'buy' && ardArm !== 'sell') return
    const handler = (e) => {
      const d = e.detail || {}
      if (!isFutures && d.token && currentToken && d.token !== currentToken) return
      if (!(d.sec != null && d.sec <= liqFireMaxSec)) return
      // `at` is tape (server) time; shift it onto the client clock with the
      // offset the chart saw at emit time, then compare to the arm time.
      const off = Number.isFinite(d.tapeAt) && Number.isFinite(d.clientAt) ? d.clientAt - d.tapeAt : 0
      if (!(Number.isFinite(d.at) && d.at + off >= ardArmAtRef.current)) return
      const side = d.dir === 'up' ? 'buy' : d.dir === 'down' ? 'sell' : null
      if (!side || ardArmRef.current !== side) return
      ardArmRef.current = null          // sync guard: two chart slots, one fire
      if (chainRef.current) chainRef.current.liqFired = true   // order-chain-20261021
      setArdArm(null)
      const s = d.sec.toFixed(1)
      if (side === 'buy') {
        showToast?.(`🤖 ${liqArmLabel} ▲ up ${s}s — firing BUY`, 'buy')
        handleBuy()
      } else {
        showToast?.(`🤖 ${liqArmLabel} ▼ down ${s}s — firing SELL`, 'sell')
        handleSell()
      }
    }
    window.addEventListener('solcheddar-liq-reaction', handler)
    return () => window.removeEventListener('solcheddar-liq-reaction', handler)
  }, [ardArm, handleBuy, handleSell, currentToken, isFutures, showToast, liqFireMaxSec, liqArmLabel])

  // Token switch clears any arm — a queue set on token A must never fire a
  // market order into token B.
  const ardPrevTokenRef = useRef(currentToken)
  useEffect(() => {
    if (ardPrevTokenRef.current === currentToken) return
    ardPrevTokenRef.current = currentToken
    if (ardArmRef.current !== null) {
      setArdArm(null)
      showToast?.(`${liqArmLabel} arm cleared — token changed`, 'sell')
    }
  }, [currentToken, showToast])

  // toggleArdArm / queueArd removed with the header glyph. The old machine
  // had an 'armed' limbo state — armed but no side chosen — that only
  // existed because arming and choosing happened in two different places.
  // One gesture now does both, so that state has no meaning and is gone;
  // ardArm is 'buy' | 'sell' | null.

  // ─── ARD arming by LONG-PRESS on BUY / SELL ───
  // The old flow was two steps in two places: click a robot glyph in the
  // wallet header to arm, then click BUY or SELL to choose a side. The glyph
  // was a live trigger sitting in a row of passive readouts, its three states
  // separated only by tint, and it duplicated queue state the BUY/SELL
  // buttons already showed.
  //
  // Now the gesture IS the control. Hold BUY for HOLD_MS and the bot mark
  // rises on the button itself; release and that side is queued on the next
  // Liq ≤3s reaction (was: ARD triangle). Press again to cancel. Arming lives on the button that
  // acts, and the icon only exists while it means something.
  //
  // A long press must NOT also fire a normal trade, so onClick is gone from
  // those buttons — pointerup decides, and it is the only path to handleBuy /
  // handleSell. That ordering is deliberate: a click handler firing after a
  // long press is exactly how you'd accidentally market-buy while arming.
  const ARD_HOLD_MS = 450
  const [ardPressing, setArdPressing] = useState(null)   // 'buy' | 'sell' | null
  const ardHoldRef = useRef({ timer: null, side: null, fired: false })
  // order-picker-20261021: the long-press on BUY opens the order picker.
  const buyBtnRef = useRef(null)
  const pickerRef = useRef(null)                 // { rect, hover } while open
  const [picker, setPicker] = useState(null)     // render copy of pickerRef
  const [buyHover, setBuyHover] = useState(false)   // buy-menu-bot-20261022: bot mark on hover while menu open
  const closePicker = useCallback(() => { pickerRef.current = null; setPicker(null) }, [])

  const cancelArdHold = useCallback(() => {
    const h = ardHoldRef.current
    if (h.timer) { clearTimeout(h.timer); h.timer = null }
    h.side = null
    setArdPressing(null)
  }, [])

  const startArdHold = useCallback((side) => {
    const h = ardHoldRef.current
    h.side = side
    h.fired = false
    if (h.timer) clearTimeout(h.timer)
    if (isCmeRef.current) return   // cmepaper: no hold-arm/order picker on CME (paper taps only)
    h.timer = setTimeout(() => {
      h.fired = true
      h.timer = null
      setArdPressing(side)          // bot mark appears: the press has taken
      try {
        if (side === 'buy' && buyBtnRef.current) {
          const rc = buyBtnRef.current.getBoundingClientRect()
          const rect = { left: rc.left, top: rc.top, right: rc.right, bottom: rc.bottom }
          pickerRef.current = { rect, hover: null }
          setPicker({ rect, hover: null })
        }
      } catch {}
      if (navigator.vibrate) { try { navigator.vibrate(12) } catch {} }
    }, ARD_HOLD_MS)
  }, [])

  // ─── order-chain-20261021: arm steps in order; next arms when one completes ───
  // Liquidity completes when its arm fires; Push / recipe report via onEnd.
  // Any cancel or failure stops the whole chain.
  const [chainRun, setChainRun] = useState(null)   // { i, n } for the status line
  const stopChain = useCallback((why) => {
    if (!chainRef.current) return
    chainRef.current = null
    setChainRun(null)
    if (why) showToast?.(`Chain stopped: ${why}`, 'sell')
  }, [showToast])
  const armChainStep = useCallback((i) => {
    const c = chainRef.current
    if (!c) return
    if (i >= c.ids.length) { chainRef.current = null; setChainRun(null); showToast?.('Chain done', 'buy'); return }
    const { push, recipe } = orderHooksRef.current
    c.i = i; c.liqArmed = false; c.liqFired = false
    setChainRun({ i, n: c.ids.length })
    let ok = false
    try {
      if (!push.running()) push.clear()          // drop the last step's 'done' line
      if (!recipe.running()) recipe.clear()
      const id = c.ids[i]
      if (push.running() || recipe.running()) ok = false
      else if (id.startsWith('r:')) { recipe.arm(id.slice(2)); ok = recipe.running() }
      else if (id === 'push') { push.arm(); ok = push.running() }
      else { c.liqArmed = true; ardArmRef.current = 'buy'; setArdArm('buy'); ok = true }
    } catch {}
    if (!ok && chainRef.current === c) stopChain(`step ${i + 1} did not arm`)
  }, [showToast, stopChain])
  chainEndRef.current = (ok) => {
    const c = chainRef.current
    if (!c) return
    if (!ok) { stopChain('step cancelled or failed'); return }
    const next = c.i + 1
    setTimeout(() => { if (chainRef.current === c) armChainStep(next) }, 0)
  }
  // Liquidity step: arm cleared -> fired = next step, otherwise cancelled = stop.
  useEffect(() => {
    const c = chainRef.current
    if (!c || ardArm || !c.liqArmed) return
    c.liqArmed = false
    if (c.liqFired) { const next = c.i + 1; setTimeout(() => { if (chainRef.current === c) armChainStep(next) }, 0) }
    else stopChain('Liquidity cancelled')
  }, [ardArm]) // eslint-disable-line react-hooks/exhaustive-deps
  const cancelChainAll = useCallback(() => {
    const { push, recipe } = orderHooksRef.current
    stopChain()
    try { if (ardArmRef.current) { ardArmRef.current = null; setArdArm(null) } } catch {}
    try { if (recipe.running()) recipe.cancel() } catch {}
    try { if (push.running()) push.cancel() } catch {}
  }, [stopChain])
  // Held arm with a chain set: start it (or, if anything is running, cancel all).
  const toggleChain = useCallback(() => {
    const { push, recipe } = orderHooksRef.current
    if (chainRef.current || ardArmRef.current || push.running() || recipe.running()) {
      cancelChainAll(); showToast?.('Chain cancelled', 'sell'); return
    }
    chainRef.current = { ids: [...chainIdsRef.current], i: 0 }
    showToast?.(`Chain armed: ${chainRef.current.ids.length} steps`, 'buy')
    armChainStep(0)
  }, [armChainStep, cancelChainAll, showToast])

  // Release decides: held long enough -> arm/disarm that side; a short tap
  // is an ordinary trade. Either way the press state is cleared.
  const endArdHold = useCallback((side, run, typeOverride) => {
    const h = ardHoldRef.current
    const held = h.fired && h.side === side
    cancelArdHold()
    h.fired = false
    if (held) {
      if (side === 'buy' && !typeOverride && chainIdsRef.current.length) { toggleChain(); return }   // order-chain-20261021
      if (side === 'buy' && String(typeOverride || orderEntry.type).startsWith('r:')) { recipeOrder.arm(String(typeOverride || orderEntry.type).slice(2)); return }   // grok-orders-20261021
      if (side === 'buy' && (typeOverride || orderEntry.type) === 'push') { pushOrder.arm(); return }   // push-order-20261021
      setArdArm(a => {
        if (a === side) {
          showToast?.(`${liqArmLabel} ${side.toUpperCase()} cancelled`, 'sell')
          return null
        }
        showToast?.(side === 'buy'
          ? `🤖 BUY armed — fires on the next ${liqArmLabel} ▲ up reaction`
          : `🤖 SELL armed — fires on the next ${liqArmLabel} ▼ down reaction`, side)
        return side
      })
      return
    }
    // Short tap while that side is already queued cancels it rather than
    // sending a live order — the queued button must never be a hair trigger.
    if (ardArm === side) {
      setArdArm(null)
      showToast?.(`${liqArmLabel} ${side.toUpperCase()} cancelled`, 'sell')
      return
    }
    run?.()
  }, [ardArm, cancelArdHold, showToast, liqArmLabel, orderEntry.type, pushOrder.arm, recipeOrder.arm, toggleChain])

  // Cleanup: a timer must never outlive the component.
  useEffect(() => () => { if (ardHoldRef.current.timer) clearTimeout(ardHoldRef.current.timer) }, [])
  // order-picker-20261021: pointer is captured on BUY, so items are hit-tested
  // with elementFromPoint (works for mouse and touch drag).
  const pickAt = (x, y) => {
    try { return document.elementFromPoint(x, y)?.closest?.('[data-oe-pick]')?.getAttribute('data-oe-pick') || null }
    catch { return null }
  }
  // drawer-remove-20261021: a row's value chip (release there = edit, no arm).
  const chipAt = (x, y) => {
    try { return document.elementFromPoint(x, y)?.closest?.('[data-oe-chip]')?.getAttribute('data-oe-chip') || null }
    catch { return null }
  }
  const inBuyRect = (e, r) => !!r && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
  // order-chain-20261021: add to the end of the chain, or remove if already in it.
  const toggleChainId = useCallback((id) => {
    if (!id || id === '__new') return
    const cur = chainIdsRef.current
    if (cur.includes(id)) return updateOrderEntry({ chain: cur.filter(x => x !== id) })
    if (cur.length >= 5) return showToast?.('Chain max 5 steps', 'sell')
    updateOrderEntry({ chain: [...cur, id] })
  }, [updateOrderEntry, showToast])
  const onBuyMove = useCallback((e) => {
    const p = pickerRef.current
    if (!p || p.edit) return
    const id = pickAt(e.clientX, e.clientY)
    const chip = chipAt(e.clientX, e.clientY)
    if (id !== p.hover || chip !== p.chip) { p.hover = id; p.chip = chip; setPicker(s => (s ? { ...s, hover: id, chip } : s)) }
  }, [])
  const abortBuyPress = useCallback(() => {
    const h = ardHoldRef.current
    closePicker()
    cancelArdHold()
    h.fired = false
  }, [closePicker, cancelArdHold])
  const onBuyUp = useCallback((e) => {
    const h = ardHoldRef.current
    if (h.suppress) { h.suppress = false; return }      // Escape already cancelled
    const p = pickerRef.current
    if (!p) return endArdHold('buy', handleBuy)         // short tap / no picker: unchanged
    if (p.edit) {                                       // menu left open: a tap on BUY arms the selection
      const tap = p.tap; p.tap = false
      if (tap && inBuyRect(e, p.rect)) { closePicker(); h.fired = true; h.side = 'buy'; return endArdHold('buy', handleBuy) }
      return
    }
    const chip = chipAt(e.clientX, e.clientY)
    if (chip) {                                         // sticky edit mode: nothing armed
      cancelArdHold()
      h.fired = false
      p.edit = chip; p.hover = null; p.chip = null
      setPicker(s => (s ? { ...s, edit: chip, hover: null, chip: null } : s))
      return
    }
    const id = pickAt(e.clientX, e.clientY)
    let near = false
    try {
      const pad = 40
      const inR = (r) => r && e.clientX >= r.left - pad && e.clientX <= r.right + pad && e.clientY >= r.top - pad && e.clientY <= r.bottom + pad
      near = inR(p.rect) || inR(document.querySelector('.oe-picker')?.getBoundingClientRect())
    } catch {}
    if (id === '__new') {                               // sticky ask-Grok input; nothing armed
      cancelArdHold()
      h.fired = false
      p.edit = '__new'; p.hover = null; p.chip = null
      setPicker(s => (s ? { ...s, edit: '__new', hover: null, chip: null } : s))
      return
    }
    // picker-release-20261021: only a release ON BUY arms. Anywhere else selects
    // (if on a row) and leaves the menu open; tap BUY to arm, click off to close.
    if (inBuyRect(e, p.rect)) { closePicker(); return endArdHold('buy', handleBuy) }
    cancelArdHold()
    h.fired = false
    if (id && (e.ctrlKey || e.metaKey)) toggleChainId(id)      // order-chain-20261021
    else if (id && id !== orderEntry.type) updateOrderEntry({ type: id })
    p.edit = '__open'; p.hover = null; p.chip = null
    setPicker(s => (s ? { ...s, edit: '__open', hover: null, chip: null } : s))
  }, [endArdHold, handleBuy, closePicker, abortBuyPress, orderEntry.type, updateOrderEntry, cancelArdHold, toggleChainId])
  const onChainOrder = useCallback((id) => {
    toggleChainId(id)
    const p = pickerRef.current
    if (p && !p.edit) { p.edit = '__open'; setPicker(s => (s ? { ...s, edit: '__open', hover: null, chip: null } : s)) }
  }, [toggleChainId])
  // picker-release-20261021: clicks inside the open menu select a row (or open its setting).
  const onPickOrder = useCallback((id, editIt) => {
    const p = pickerRef.current
    if (!p) return
    if (id !== '__new' && id !== orderEntry.type) updateOrderEntry({ type: id })
    const ed = editIt ? id : '__open'
    p.edit = ed; p.hover = null; p.chip = null
    setPicker(s => (s ? { ...s, edit: ed, hover: null, chip: null } : s))
  }, [orderEntry.type, updateOrderEntry])
  useEffect(() => {
    if (!picker) return
    if (picker.edit) {
      const onKeyE = (e) => { if (e.key === 'Enter' || e.key === 'Escape') closePicker() }
      const onDown = (e) => { if (!e.target?.closest?.('.oe-picker') && !buyBtnRef.current?.contains?.(e.target)) closePicker() }   // picker-release-20261021: BUY tap arms, not closes
      window.addEventListener('keydown', onKeyE)
      document.addEventListener('pointerdown', onDown, true)
      return () => { window.removeEventListener('keydown', onKeyE); document.removeEventListener('pointerdown', onDown, true) }
    }
    const onKey = (e) => {
      if (e.key !== 'Escape') return
      abortBuyPress()
      ardHoldRef.current.suppress = true
      setTimeout(() => { ardHoldRef.current.suppress = false }, 1500)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [picker, abortBuyPress, closePicker])

  // drawer-remove-20261021: one-line status under BUY (Push run or Liquidity arm).
  const orderStatusBase = recipeOrder.status ? recipeOrder.status.text : pushOrder.status
    ? `Push: ${pushOrder.status.text}`
    : ardArm === 'buy' ? `${liqArmLabel} BUY armed`
    : ardArm === 'sell' ? `${liqArmLabel} SELL armed` : null
  const orderStatusText = chainRun ? `Chain ${chainRun.i + 1}/${chainRun.n} \u00b7 ${orderStatusBase || 'arming'}` : orderStatusBase
  const [buyRect, setBuyRect] = useState(null)
  useEffect(() => {
    if (!orderStatusText) { setBuyRect(null); return }
    let raf = 0, last = ''
    const loop = () => {
      try {
        const el = buyBtnRef.current
        if (el) {
          const rc = el.getBoundingClientRect()
          const k = `${rc.left}|${rc.top}|${rc.right}|${rc.bottom}`
          if (k !== last) { last = k; setBuyRect(rc.width ? { left: rc.left, top: rc.top, right: rc.right, bottom: rc.bottom } : null) }
        }
      } catch {}
      raf = requestAnimationFrame(loop)
    }
    loop()
    return () => cancelAnimationFrame(raf)
  }, [!!orderStatusText]) // eslint-disable-line react-hooks/exhaustive-deps
  const cancelOrderStatus = useCallback(() => {
    try {
      if (chainRef.current) { cancelChainAll(); showToast?.('Chain cancelled', 'sell'); return }   // order-chain-20261021
      if (recipeOrder.status) { recipeOrder.status.phase === 'done' ? recipeOrder.clear() : recipeOrder.cancel(); return }
      if (pushOrder.status) { pushOrder.status.phase === 'done' ? pushOrder.clear() : pushOrder.cancel(); return }
      if (ardArm) { setArdArm(null); showToast?.(`${liqArmLabel} ${ardArm.toUpperCase()} cancelled`, 'sell') }
    } catch {}
  }, [pushOrder, recipeOrder, ardArm, liqArmLabel, showToast, cancelChainAll])
  useEffect(() => { try { setOrderStatus(orderStatusText) } catch {} }, [orderStatusText])   // for Grok's snapshot

  // ─── Listen for HL positions ───
  useEffect(() => {
    if (!isFutures) return
    const handler = (e) => setHlPositions(e.detail || [])
    window.addEventListener('solcheddar-hl-positions', handler)
    return () => window.removeEventListener('solcheddar-hl-positions', handler)
  }, [isFutures])

  // ─── Listen for HL open orders ───
  useEffect(() => {
    if (!isFutures) return
    const handler = (e) => setHlOrders(e.detail || [])
    window.addEventListener('solcheddar-hl-orders', handler)
    return () => window.removeEventListener('solcheddar-hl-orders', handler)
  }, [isFutures])

  // ─── Listen for HL account balance (broadcast from useTrading poll) ───
  useEffect(() => {
    if (!isFutures) return
    const handler = (e) => {
      const avail = parseFloat(e.detail?.available || 0)
      if (Number.isFinite(avail)) setHlAvailable(avail)
    }
    window.addEventListener('solcheddar-hl-balance', handler)
    return () => window.removeEventListener('solcheddar-hl-balance', handler)
  }, [isFutures])

  // ─── Broadcast current SOL wallet holdings ───
  // Other components (BotSettingsMenu's auto-pilot list) need to filter
  // localStorage position records by the wallet's actual current balances —
  // otherwise stale entries from closed positions show up forever. We emit
  // just the mints that have a non-zero balance; payload stays small.
  useEffect(() => {
    if (isFutures) return
    const held = holdings.filter(h => (h.uiAmount || 0) > 0)
    window.dispatchEvent(new CustomEvent('solcheddar-wallet-holdings', {
      detail: {
        mints: held.map(h => h.mint),
        // Amounts let listeners tell a REAL position from dust remainder —
        // presence alone counted every leftover crumb as an open position.
        amounts: held.map(h => ({ mint: h.mint, uiAmount: h.uiAmount || 0 })),
      }
    }))
  }, [holdings, isFutures])

  // ─── HL cancel order ───
  const handleCancelHlOrder = useCallback((order) => {
    setCancellingId(order.orderId)
    window.dispatchEvent(new CustomEvent('solcheddar-hl-cancel-order', { detail: order }))
    setTimeout(() => {
      setHlOrders(prev => prev.filter(o => o.orderId !== order.orderId))
      setCancellingId(null)
    }, 1500)
  }, [])

  // ─── HL close position ───
  const handleCloseHlPosition = useCallback((pos) => {
    setClosingPos(pos.symbol)
    window.dispatchEvent(new CustomEvent('solcheddar-hl-close-position', { detail: pos }))
    setTimeout(() => setClosingPos(null), 3000)
  }, [])

  // ─── Solana: cancel limit order (delegates to useTrading via event) ───
  const [cancellingLimitId, setCancellingLimitId] = useState(null)
  const handleCancelSolanaLimit = useCallback((lo) => {
    setCancellingLimitId(lo.id)
    window.dispatchEvent(new CustomEvent('solcheddar-cancel-limit-order', { detail: lo }))
    setTimeout(() => setCancellingLimitId(null), 3000)
  }, [])

  // ─── Solana: fetch prices ───
  // One batched read (fetchMarketBatch): price, SOL price, mcap, symbol.
  const fetchPrices = useCallback((mints) => fetchMarketBatch(mints), [])

  // ─── Solana: fetch holdings ───
  // opts.silent: background refresh — no spinner, and a failure keeps the
  // current list instead of blanking it. seq: only the NEWEST fetch may
  // commit, so a slow early read can't overwrite a later, settled one.
  const holdingsSeqRef = useRef(0)
  // Bumped on every successful holdings read — the open LP panel re-reads
  // its on-chain balances off it (MeteoraLpPanel refreshKey).
  const [holdingsTick, setHoldingsTick] = useState(0)
  const holdingsRetryRef = useRef({ timer: 0, n: 0 })
  useEffect(() => () => clearTimeout(holdingsRetryRef.current.timer), [])
  const fetchHoldingsRef = useRef(null)   // latest fetchHoldings, for the retry timer
  const fetchHoldings = useCallback(async (opts = {}) => {
    if (!publicKey) {
      setHoldings([])
      return
    }
    const seq = ++holdingsSeqRef.current
    if (!opts.silent) setLoading(true)
    try {
      // strict: an RPC error throws instead of reading as "no tokens".
      const raw = await getAllTokenHoldings(publicKey, { strict: true })
      const mints = raw.map(t => t.mint)
      const prices = await fetchPrices(mints)
      // Names come with the batch; only mints it missed (no DEX pair) fall
      // back to getTokenName, once each — cached, not every 30s per row.
      const names = await Promise.all(raw.map(async (t) => {
        const known = prices[t.mint]?.symbol || NAME_CACHE.get(t.mint)
        if (known) return known
        try {
          const n = await getTokenName(t.mint)
          if (n && !n.endsWith('...')) NAME_CACHE.set(t.mint, n)
          return n
        } catch { return t.mint.slice(0, 6) + '...' }
      }))

      const withData = raw.map((t, i) => {
        const price = prices[t.mint] || { priceUsd: 0 }
        const currentPriceUsd = price.priceUsd
        const valueUsd = t.uiAmount * currentPriceUsd
        const pos = getStoredPosition(t.mint)
        const entryPriceUsd = getEntryUsdPrice(pos)

        let pnlUsd = null, pnlPct = null
        if (entryPriceUsd && entryPriceUsd > 0 && currentPriceUsd > 0) {
          const costBasis = t.uiAmount * entryPriceUsd
          pnlUsd = valueUsd - costBasis
          pnlPct = ((currentPriceUsd - entryPriceUsd) / entryPriceUsd) * 100
        }

        return { ...t, name: names[i], priceUsd: currentPriceUsd, valueUsd, pnlUsd, pnlPct,
                 priceNative: price.priceNative || 0, mcapUsd: price.mcapUsd || 0 }
      })

      withData.sort((a, b) => b.valueUsd - a.valueUsd)
      if (seq === holdingsSeqRef.current) {
        setHoldings(withData)
        setHoldingsTick(t => t + 1)
        holdingsRetryRef.current.n = 0
      }
    } catch (err) {
      // A failed read is UNKNOWN, not empty: keep the current list (an empty
      // one after a reload stays empty) and retry soon — 2s, 5s, 10s — rather
      // than waiting for the 30s safety poll. The old path blanked the list,
      // and the lenient read had already turned RPC errors into "no tokens".
      console.error('Failed to fetch holdings:', err)
      if (seq === holdingsSeqRef.current) {
        const r = holdingsRetryRef.current
        clearTimeout(r.timer)
        if (r.n < 3) {
          const ms = [2000, 5000, 10000][r.n++]
          r.timer = setTimeout(() => fetchHoldingsRef.current?.({ silent: true }), ms)
        }
      }
    }
    if (seq === holdingsSeqRef.current) setLoading(false)
  }, [publicKey, fetchPrices])
  fetchHoldingsRef.current = fetchHoldings

  useEffect(() => {
    holdingsRetryRef.current.n = 0
    if (open && publicKey && !isFutures) fetchHoldings()
  }, [open, publicKey, isFutures, fetchHoldings])

  // ─── Stay current on every trade ───
  // RPC reads lag a landed tx, so ONE refetch at the event reads the old
  // amounts and nothing corrects them. Refetch holdings + SOL in stages until
  // they settle. Triggers: this app's own buys/sells/closes, and the server's
  // wallet-activity feed (any trade by this wallet — bots, other windows,
  // other apps). A new trigger restarts the stages. Plus a 30s safety poll.
  const refreshTimersRef = useRef([])
  useEffect(() => {
    if (!open || !publicKey || isFutures) return
    const clear = () => { refreshTimersRef.current.forEach(clearTimeout); refreshTimersRef.current = [] }
    const refreshAfterTx = () => {
      clear()
      refreshTimersRef.current = [0, 1500, 4000, 10000].map(ms => setTimeout(() => {
        fetchHoldings({ silent: true })
        balanceFetchRef.current?.()
      }, ms))
    }
    const evs = ['solcheddar-buy-executed', 'solcheddar-sell-executed',
                 'solcheddar-position-closed', 'solcheddar-wallet-position-updated']
    evs.forEach(e => window.addEventListener(e, refreshAfterTx))
    const safety = setInterval(() => fetchHoldings({ silent: true }), 30000)
    return () => {
      evs.forEach(e => window.removeEventListener(e, refreshAfterTx))
      clearInterval(safety)
      clear()
    }
  }, [open, publicKey, isFutures, fetchHoldings])

  // Listen for position-closed events (from chart tag X button) to clear from holdings
  useEffect(() => {
    const handler = (e) => {
      const mint = e.detail?.mint
      if (mint) setHoldings(prev => prev.filter(t => t.mint !== mint))
    }
    window.addEventListener('solcheddar-position-closed', handler)
    return () => window.removeEventListener('solcheddar-position-closed', handler)
  }, [])

  // ─── Close position (uses same trading hook as BUY/SELL) ───
  const handleClose = useCallback(async (holding) => {
    if (!holding) return

    setClosing(holding.mint)

    try {
      if (isFutures) {
        const pos = hlPositions.find(p => p.symbol === holding.mint)
        if (pos) {
          window.dispatchEvent(new CustomEvent('solcheddar-hl-close-position', { detail: pos }))
        }
        setHoldings(prev => prev.filter(t => t.mint !== holding.mint))
      } else {
        // Always 100% sell on X close
        const pos = trading.positions.find(p => p.token === holding.mint)
        if (pos) {
          // handleClosePosition does 100% sell + fires position-closed event
          await trading.handleClosePosition(pos)
        } else {
          // No tracked position record for this row (bought elsewhere /
          // record cleared). MUST sell THIS row's mint explicitly —
          // executeMarketOrder('sell') is bound to the CHARTED token, so
          // using it here sold whatever was on the chart, not the row. Bug.
          showToast?.(`Selling all ${holding.name}...`, 'sell')
          await executeSell(holding.mint, 100, publicKey, signTransaction, null)
          // A sell can change LP state (and will, once add/withdraw land).
          if (publicKey) refreshLpPositions(publicKey.toString())
          window.dispatchEvent(new CustomEvent('solcheddar-position-closed', { detail: { mint: holding.mint } }))
        }
        setHoldings(prev => prev.filter(t => t.mint !== holding.mint))
      }
    } catch (err) {
      if (err.message === 'No tokens to sell') {
        setHoldings(prev => prev.filter(t => t.mint !== holding.mint))
        window.dispatchEvent(new CustomEvent('solcheddar-position-closed', { detail: { mint: holding.mint } }))
      } else {
        showToast?.(`Close failed: ${err.message}`, 'sell')
      }
    } finally {
      setClosing(null)
    }
  }, [trading, isFutures, hlPositions, showToast, publicKey, signTransaction])

  const formatAmount = (n) => {
    if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B'
    if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M'
    if (n >= 1e3) return (n / 1e3).toFixed(2) + 'K'
    if (n >= 1) return n.toFixed(2)
    if (n > 0) return n.toFixed(6)
    return '0'
  }

  const formatUsd = (n) => {
    if (n >= 1000) return '$' + (n / 1000).toFixed(1) + 'K'
    if (n >= 1) return '$' + n.toFixed(2)
    if (n > 0) return '$' + n.toFixed(4)
    return '$0'
  }

  const formatPnl = (n) => {
    const sign = n >= 0 ? '+' : ''
    if (Math.abs(n) >= 1000) return sign + '$' + (n / 1000).toFixed(1) + 'K'
    return sign + '$' + n.toFixed(2)
  }

  const formatMcapCompact = (n) => {
    if (n >= 1e9) return (n / 1e9).toFixed(2) + 'B'
    if (n >= 1e6) return (n / 1e6).toFixed(2) + 'M'
    if (n >= 1e3) return (n / 1e3).toFixed(1) + 'K'
    if (n >= 1) return '$' + n.toFixed(2)
    if (n > 0) return '$' + n.toFixed(4)
    return '0'
  }

  // ─── Merge wallet holding with trading position (single source of truth for entry data) ───
  const walletHolding = holdings.find(h => h.mint === currentToken)
  const tradingPos = !isFutures ? trading.positions.find(p => p.token === currentToken) : null

  // Live values from the chart's useChartData object — exactly the same
  // source the position tag in FootprintChartCanvas reads:
  //   - data.chartData.highlightMcap → React STATE, re-renders this
  //     component on every tick (RAF-coalesced inside useChartData)
  //   - data.solPriceRef.current → ref, latest SOL→USD
  // Without state changing per tick the component wouldn't re-render
  // and the math wouldn't re-run; without the ref we'd need a slower
  // polled SOL price.
  const liveMcap   = data?.chartData?.highlightMcap || 0
  cmePxRef.current = liveMcap   // cmepaper: futures 'mcap' is the last price
  const liveSolUsd = data?.solPriceRef?.current || 0

  const currentHolding = (() => {
    if (!walletHolding && !tradingPos) return null
    // Start with wallet data if we have it
    const base = walletHolding || {
      mint: currentToken,
      name: currentToken?.slice(0, 6) + '...',
      uiAmount: tradingPos?.tokenAmount || 0,
      priceUsd: 0,
      valueUsd: 0,
      pnlUsd: null,
      pnlPct: null,
    }
    // If trading position exists, prefer its tokenAmount (updates instantly on partial sells)
    if (tradingPos) {
      const displayAmount = tradingPos.tokenAmount
      const supply = tradingPos.tokenSupply > 0 ? tradingPos.tokenSupply : DEFAULT_PUMP_SUPPLY
      // Live P/L via the shared helper — IDENTICAL math to the on-chart
      // position tag (SOL-based, converted at the current SOL price). This
      // is what keeps the wallet number and the tag number in lock-step.
      const { pnlUsd, pnlPct, currentValueUsd } = solPositionPnl({
        entryMcap: tradingPos.entryMcap,
        currentMcap: liveMcap,
        tokenAmount: displayAmount,
        tokenSupply: supply,
        solPriceUsd: liveSolUsd,
      })
      // Fall back to the polled wallet value only before the first live tick
      const displayValueUsd = currentValueUsd > 0 ? currentValueUsd : base.valueUsd
      return { ...base, uiAmount: displayAmount, valueUsd: displayValueUsd, pnlUsd, pnlPct, entryMcap: tradingPos.entryMcap, hasTradeEntry: true }
    }
    return base
  })()
  // Every mint that has its own row below — its LPs render as LpLines on
  // that row, and LP POSITIONS lists only the rest. No double-showing, and
  // no position without a home (the current-token row now renders its LPs).
  // Current-token row: same fields as every other row, but the LIVE chart
  // mcap (data.chartData.highlightMcap, SOL-denominated) and the trade
  // record's amount/P/L where the chart has them — fresher than the 30s poll.
  const currentDetails = (() => {
    if (!currentHolding) return null
    const base = walletHolding
      ? holdingDetails({ ...walletHolding, uiAmount: currentHolding.uiAmount }, liveSolUsd)
      : holdingDetails({ mint: currentHolding.mint, uiAmount: currentHolding.uiAmount, priceUsd: 0 }, liveSolUsd)
    const liveMcapUsd = liveMcap > 0 && liveSolUsd > 0 ? liveMcap * liveSolUsd : 0
    const valueUsd = currentHolding.valueUsd > 0 ? currentHolding.valueUsd : base.valueUsd
    let entryMcapUsd = base.entryMcapUsd
    let hasEntry = base.hasEntry
    if (tradingPos?.entryMcap > 0) {
      hasEntry = true
      const esp = tradingPos.entrySolPrice || getStoredPosition(currentHolding.mint)?.entrySolPrice
      entryMcapUsd = esp > 0 ? tradingPos.entryMcap * esp : entryMcapUsd
    }
    return {
      ...base,
      amount: currentHolding.uiAmount || 0,
      valueUsd,
      valueSol: liveSolUsd > 0 ? valueUsd / liveSolUsd : base.valueSol,
      mcapUsd: liveMcapUsd || base.mcapUsd,
      hasEntry, entryMcapUsd,
      // Trade-tracked position: the live chart P/L (same as the tag).
      pnlUsd: tradingPos ? currentHolding.pnlUsd : base.pnlUsd,
      pnlPct: tradingPos ? currentHolding.pnlPct : base.pnlPct,
    }
  })()
  // ─── Other holdings, by USD value (desc), unknown value last ───
  // The current-token row stays pinned on top (its own block). Tiebreak by
  // mint so equal values never swap. Hysteresis: the previous order is kept
  // unless a row now out-values the one above it by >10%, a value becomes
  // (un)known, or the set of tokens changes — so small price moves on the
  // 30s refresh don't shuffle rows.
  const holdingsOrderRef = useRef([])
  const otherHoldings = (() => {
    const rows = holdings.filter(h => h.mint !== currentToken)
    const val = new Map(rows.map(t => {
      const v = (t.uiAmount || 0) * (t.priceUsd || 0)
      return [t.mint, v > 0 && Number.isFinite(v) ? v : -1]
    }))
    const byMint = new Map(rows.map(t => [t.mint, t]))
    const prev = holdingsOrderRef.current
    const sameSet = prev.length === rows.length && prev.every(m => byMint.has(m))
    const outOfOrder = !sameSet || prev.some((m, i) => {
      if (i === 0) return false
      const above = val.get(prev[i - 1]), cur = val.get(m)
      if (cur < 0) return false               // unknown below anything: fine
      if (above < 0) return true              // known below unknown: fix
      return cur > above * 1.1
    })
    if (outOfOrder) {
      holdingsOrderRef.current = rows
        .map(t => t.mint)
        .sort((a, b) => (val.get(b) - val.get(a)) || (a < b ? -1 : a > b ? 1 : 0))
    }
    return holdingsOrderRef.current.map(m => byMint.get(m))
  })()
  const lpRowMints = new Set(holdings.map(h => h.mint))
  if (currentHolding?.mint) lpRowMints.add(currentHolding.mint)
  const lpOrphans = lpPositions.filter(lp => !lp.mintX || !lpRowMints.has(lp.mintX))

  // Solana limit orders for current token
  const solanaLimitOrders = !isFutures
    ? trading.limitOrders.filter(lo => lo.token === currentToken && !lo.futuresOrder)
    : []

  const tokenDisplayName = isStk ? `${stkInfo(hlPair?.base).sym.replace(/\s+/g, ' ')} · SCHWAB` : isCme ? `${cmeSym} · PAPER` : isFutures
    ? (hlPositions[0]?.symbol || 'Futures')
    : (currentHolding?.name || currentToken?.slice(0, 6) + '...' || 'Select Token')

  // Panel position when open - base is (10, 175) with offset, or buttonPos when not pinned.
  // While a drag is active, read the live ref position so the frequent
  // liveMcap re-renders reflect the in-progress drag instead of snapping back.
  const dragActive = dragRef.current.active && dragRef.current.liveLeft != null
  const panelX = dragActive
    ? dragRef.current.liveLeft
    : held ? heldPos.x : (pinned ? 10 + dragOffset.x : buttonPos.x)
  const panelY = dragActive
    ? dragRef.current.liveTop
    : held ? heldPos.y : (pinned ? 175 + dragOffset.y : buttonPos.y)

  const walletLocked = !isFutures && !publicKey

  // ── Extracted controls (so phone panel mode can reposition them) ──
  // Buy/Sell row — futures relabels by position state (no hedging). Computed
  // once here; rendered at the very top in phone panel mode, or in its normal
  // place on desktop.
  const buySellRow = (() => {
    let curHlPos = null
    if (isFutures && hlPositions.length > 0) {
      if (hlPair?.base) {
        const base = hlPair.base
        const baseU = base.toUpperCase()
        curHlPos = hlPositions.find(p => p.symbol === base)
          || hlPositions.find(p => (p.symbol || '').toUpperCase() === baseU)
          || hlPositions.find(p => (p.symbol || '').toUpperCase().startsWith(baseU))
      }
      // NO single-position fallback here. Grabbing the lone HL position
      // regardless of symbol match labeled the buttons FLAT/REDUCE on a
      // chart showing a DIFFERENT pair — an accidental close/reduce sent
      // from the wrong chart. No match = treat as flat; the position's own
      // pair still shows the correct state when charted.
    }
    if (isCme) curHlPos = cmePos   // paper position drives LONG/SHORT/FLAT labels
    const futuresState = curHlPos
      ? ((curHlPos.side || '').toUpperCase() === 'LONG' ? 'long' : 'short')
      : 'flat'
    let buyLabel = 'BUY'
    let sellLabel = 'SELL'
    let buyCls = 'buy'
    let sellCls = 'sell'
    if (isFutures && !isStk) {
      const reducing = defaults.sellPercentage < 100
      const closeLabel = reducing ? 'REDUCE' : 'FLAT'
      const closeCls = reducing ? 'reduce' : 'flat'
      if (futuresState === 'flat') {
        buyLabel = 'LONG';  sellLabel = 'SHORT'
      } else if (futuresState === 'long') {
        buyLabel = 'LONG'
        sellLabel = closeLabel
        sellCls = closeCls
      } else if (futuresState === 'short') {
        buyLabel = closeLabel
        buyCls = closeCls
        sellLabel = 'SHORT'
      }
    }
    return (
      <div className="wallet-toolbar-row" style={{ padding: '4px 6px', position: 'relative', overflow: 'visible' }}>
        <button
          className={`wallet-tb-btn ${buyCls} ${isBuying ? 'active' : ''} ${(ardArm === 'buy' || pushOrder.armed) ? 'ard-queued ard-q-buy' : ''} ${(ardPressing === 'buy' || (picker?.edit && buyHover)) ? 'ard-pressing' : ''}`}
          ref={buyBtnRef}
          style={{ touchAction: 'none' }}
          onPointerDown={(e) => { ardHoldRef.current.suppress = false; e.currentTarget.setPointerCapture?.(e.pointerId); if (pickerRef.current?.edit) { pickerRef.current.tap = true; return } startArdHold('buy') }}
          onPointerMove={onBuyMove}
          onPointerUp={onBuyUp}
          onPointerEnter={() => setBuyHover(true)}
          onPointerLeave={() => { setBuyHover(false); if (!pickerRef.current) cancelArdHold() }}
          onPointerCancel={abortBuyPress}
          onContextMenu={(e) => e.preventDefault()}
          disabled={isBuying || (!isFutures && !currentToken)}
          title={ardArm === 'buy'
            ? `Queued on the next ${liqArmLabel} ▲ up reaction — tap to cancel`
            : `Tap to buy · hold to arm on the next ${liqArmLabel} ▲ up reaction`}
        >
          {(ardPressing === 'buy' || ardArm === 'buy' || pushOrder.armed || (picker?.edit && buyHover)) && (
            <span className="wallet-tb-bot"><ArdBotIcon size={12} /></span>
          )}
          {isBuying ? '...' : ardArm === 'buy' ? 'BUY ▲' : buyLabel}
        </button>
        {picker && <OrderPicker rect={picker.rect} hover={picker.hover} chipHover={picker.chip} edit={picker.edit} selected={orderEntry.type} cfg={orderEntry} update={updateOrderEntry} orders={pickerOrders} onDelete={onDeleteOrder} onAsk={onAskOrder} onPick={onPickOrder} chain={chainIds} onChain={onChainOrder} />}
        {orderStatusText && !picker && <OrderStatusLine rect={buyRect} anchor={buyBtnRef.current} text={orderStatusText} onCancel={cancelOrderStatus} />}
        <button
          className={`wallet-tb-btn ${sellCls} ${isSelling ? 'active' : ''} ${ardArm === 'sell' ? 'ard-queued ard-q-sell' : ''} ${ardPressing === 'sell' ? 'ard-pressing' : ''}`}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); startArdHold('sell') }}
          onPointerUp={() => endArdHold('sell', handleSell)}
          onPointerLeave={cancelArdHold}
          onPointerCancel={cancelArdHold}
          onContextMenu={(e) => e.preventDefault()}
          disabled={isSelling || (!isFutures && !currentToken)}
          title={ardArm === 'sell'
            ? `Queued on the next ${liqArmLabel} ▼ down reaction — tap to cancel`
            : `Tap to sell · hold to arm on the next ${liqArmLabel} ▼ down reaction`}
        >
          {(ardPressing === 'sell' || ardArm === 'sell') && (
            <span className="wallet-tb-bot"><ArdBotIcon size={12} /></span>
          )}
          {isSelling ? '...' : ardArm === 'sell' ? 'SELL ▼' : sellLabel}
        </button>
      </div>
    )
  })()

  // QTY input — in phone panel mode this is placed in the titlebar (replacing
  // the X); on desktop it stays in the QTY row below.
  const qtyInput = (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      <input
        type="number"
        className="wallet-input"
        value={displayedQty}
        onChange={(e) => {
          const value = parseFloat(e.target.value) || 0
          if (unitMode) setCenterQty(value)
          else if (isFutures) {
            const maxQty = (!isCme && hlAvailable > 0) ? hlAvailable : Infinity
            setFutQty(Math.max(0.0001, Math.min(value, maxQty)))
          } else {
            setDefaults({ buyAmountSol: Math.max(0.001, value) })
          }
        }}
        onWheel={handleQuantityWheel}
        step={unitMode ? 1 : isFutures ? 10 : 0.05}
        min={unitMode ? 1 : isFutures ? 0.0001 : 0.001}
        title={qtyUnit === 'sh' ? 'Shares' : qtyUnit ? 'Contracts' : undefined}
      />
      {qtyUnit && <span className="wallet-qty-unit" style={{ fontSize: 10, opacity: 0.7 }}>{qtyUnit === 'sh' ? 'SH' : 'CT'}</span>}
      <KnurledWheel
        value={displayedQty}
        snap={false}
        step={unitMode ? 1 : isFutures ? 10 : 0.05}
        min={unitMode ? 1 : isFutures ? 0.0001 : 0.001}
        max={isFutures ? ((!unitMode && hlAvailable > 0) ? hlAvailable : Infinity) : Infinity}
        onChange={(v) => setCenterQty(unitMode ? v : Math.round(v * 1000) / 1000)}
        width={22}
        height={45}
        ariaLabel={qtyUnit === 'sh' ? 'Shares' : qtyUnit ? 'Contracts' : isFutures ? 'Margin' : 'Quantity'}
      />
      <MarketLimitToggle
        mode={orderMode}
        onChange={setOrderMode}
        scale={0.42}
      />
    </span>
  )

  return (
    <>
      {/* Control-style right-click menu (dial / slider, per section) */}
      {controlMenu && createPortal(
        <div
          ref={controlMenuRef}
          onMouseDown={(e) => e.stopPropagation()}
          onContextMenu={(e) => e.preventDefault()}
          style={{
            position: 'fixed',
            // Screen-size aware: pin to the click point but keep the whole
            // menu on-screen (≈118×80 + 8px margin). Identical on desktop
            // (always has room) and phone (where the raw click coords would
            // otherwise push it off the right/bottom edge).
            left: Math.max(8, Math.min(controlMenu.x, window.innerWidth  - 126)),
            top:  Math.max(8, Math.min(controlMenu.y, window.innerHeight - (asPanel ? 92 : 126))),
            // Portaled to <body> (controls-menu-20261024): inside .header it
            // was hidden with the header (chart max / phone Focus). At body
            // level 70 sits above the body-portaled station (60).
            zIndex: 70,
            background: '#16181c',
            border: '1px solid #2a2d33',
            borderRadius: 6,
            boxShadow: '0 6px 20px rgba(0,0,0,0.5)',
            padding: '4px',
            minWidth: 110,
            fontFamily: 'inherit',
          }}
        >
          <div style={{ fontSize: 8, fontWeight: 700, color: '#555', letterSpacing: '0.06em', textTransform: 'uppercase', padding: '4px 8px 2px' }}>
            {isFutures ? 'Futures' : 'Solana'} Controls
          </div>
          {['dial', 'slider'].map((k) => (
            <div
              key={k}
              onClick={() => { setActiveControlStyle(k); setControlMenu(null) }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                color: activeControlStyle === k ? '#fff' : '#bbb',
                background: activeControlStyle === k ? '#22252b' : 'transparent',
              }}
              onMouseEnter={(e) => { if (activeControlStyle !== k) e.currentTarget.style.background = '#1c1f24' }}
              onMouseLeave={(e) => { if (activeControlStyle !== k) e.currentTarget.style.background = 'transparent' }}
            >
              <span style={{ textTransform: 'capitalize' }}>{k}</span>
              {activeControlStyle === k && <span style={{ color: '#44cc77', fontSize: 12 }}>✓</span>}
            </div>
          ))}
          {!asPanel && (
            <div
              onClick={() => { toggleHold(); setControlMenu(null) }}
              title={holdPref ? 'Scrolls with the app' : 'Floats above the scroll'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
                padding: '6px 8px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                marginTop: 2, borderTop: '1px solid #2a2d33',
                color: holdPref ? '#fff' : '#bbb',
                background: holdPref ? '#22252b' : 'transparent',
              }}
              onMouseEnter={(e) => { if (!holdPref) e.currentTarget.style.background = '#1c1f24' }}
              onMouseLeave={(e) => { if (!holdPref) e.currentTarget.style.background = 'transparent' }}
            >
              <span>Hold position</span>
              <span style={{ fontSize: 13, lineHeight: 1, color: holdPref ? '#44cc77' : '#666' }}>{holdPref ? '☑' : '☐'}</span>
            </div>
          )}
        </div>,
        document.body
      )}
      {/* Toggle button — hidden in panel mode (controls render inline). */}
      {!asPanel && (
      <button
        ref={buttonRef}
        className="wallet-holdings-toggle"
        onClick={() => {
          if (open) { startClose(); return }   // close through the fold, same as ✕
          if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            setButtonPos({ x: rect.left, y: rect.bottom + 5 })
          }
          // NO position reset here any more. This used to snap the panel back to
          // the pinned default (10, 175) on EVERY click — which is the single
          // biggest reason a reopen never came home. Where the panel lives is the
          // panel's business; the toggle only decides whether it's on screen.
          setPinned(true)
          setOpen(true)
        }}
        title={isFutures ? 'Positions & Orders' : 'Wallet Holdings'}
        style={{ position: 'relative' }}
      >
        <img src="/Wallet1.png" alt="holdings" className="wallet-logo" />
      </button>
      )}

      {/* Panel - styled like BotSettingsMenu avwap-toolbar. Always shown in
          panel mode; rendered inline via the static-position override below. */}
      {/* Floating (non-panel) station is portaled to <body>: inside .header it was
          trapped/hidden when a chart is maxed (phone Focus collapses the header to
          0 + opacity 0; desktop max hides header children). At body level its
          z-index 60 sits above the chart cell (20) and the Focus chart (51). */}
      {(open || asPanel) && ((node) => (asPanel ? node : createPortal(node, held ? holdHost : document.body)))(
        <>
          <style dangerouslySetInnerHTML={{__html: `
            .wallet-positions-list-scroll::-webkit-scrollbar {
              width: 4px !important;
              background: #16161a !important;
            }
            .wallet-positions-list-scroll::-webkit-scrollbar-track {
              background: #16161a !important;
            }
            .wallet-positions-list-scroll::-webkit-scrollbar-thumb {
              background: #2a2a35 !important;
              border-radius: 2px !important;
            }
            .wallet-positions-list-scroll::-webkit-scrollbar-thumb:hover {
              background: #3a3a45 !important;
            }
          `}} />
          <style>{`
            /* Phone panel: BUY/SELL row pinned at the very top, above the
               header; QTY input sitting in the titlebar where the X was. */
            .wallet-panel-topbtns {
              padding: 4px 6px 0;
            }
            .wallet-titlebar-qty {
              display: flex;
              align-items: center;
              gap: 4px;
              margin-left: auto;
            }
            .wallet-titlebar-qty .wallet-input {
              width: 70px;
              text-align: right;
            }
            .wallet-toolbar {
              position: fixed;
              display: flex;
              flex-direction: column;
              width: 250px;
              /* Chart's frame-and-ground so the panel reads as part of the
                 instrument, not a widget parked on it: same --surface,
                 one --seam hairline (no bevel), a faint shadow only for
                 float separation — the heavy 8px drop was the loudest
                 "separate object" cue. Insides (churn, sliders, buttons)
                 are deliberately untouched. */
              background: var(--surface, #12121a);
              border: 1px solid var(--seam, rgba(200,210,230,0.08));
              border-radius: 6px;
              box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
              z-index: 60;
              user-select: none;
            }
            .wallet-toolbar-row {
              display: flex;
              flex-wrap: nowrap;
              align-items: center;
              padding: 4px 6px;
              gap: 4px;
              border-bottom: 1px solid var(--seam, rgba(200,210,230,0.08));
            }
            .wallet-toolbar-row:last-child {
              border-bottom: none;
            }
            .wallet-menu-titlebar {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 6px 8px 5px 10px;
              background: var(--surface-rail, #14141d);
              border-bottom: 1px solid var(--seam, rgba(200,210,230,0.08));
              border-radius: 6px 6px 0 0;
              cursor: grab;
              user-select: none;
            }
            .wallet-menu-titlebar:active { cursor: grabbing; }
            .wallet-menu-title {
              font-size: 10px;
              font-weight: 600;
              color: #8a8f9c;
              letter-spacing: 0.12em;
              text-transform: uppercase;
              font-family: 'JetBrains Mono', monospace;
            }
            .wallet-menu-close {
              background: none;
              border: none;
              color: #555;
              font-size: 13px;
              cursor: pointer;
              padding: 0 4px;
              line-height: 1;
              transition: color 0.1s;
            }
            .wallet-menu-close:hover { color: #ff5252; }
            .wallet-tb-btn {
              position: relative;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 4px;
              background: none;
              border: 1px solid transparent;
              color: #aaa;
              font-size: 11px;
              font-weight: 600;
              letter-spacing: 0.03em;
              padding: 4px 10px;
              corner-shape: bevel;
              border-radius: 5px 0 5px 0; /* match current-price chip shape */
              cursor: pointer;
              transition: all 0.1s;
              white-space: nowrap;
              flex: 1;
              overflow: hidden;
            }
            /* Accent stripe and slanted corner like the current-price chip */
            .wallet-tb-btn::before {
              content: "";
              position: absolute;
              left: 0; top: 0; bottom: 0;
              width: 6px;
              z-index: 5;
              background: transparent;
              clip-path: polygon(0 0, 100% 0, 0 90%);
            }
            .wallet-tb-btn:hover { color: #ddd; background: #22222a; border-color: #33333d; }
            .wallet-tb-btn.active { color: #fff; background: #25252f; border-color: #3a3a50; }
            /* ── ARD arm: bot icon + queued-button state ── */
            .wallet-ard-arm {
              width: 26px; min-width: 26px; height: 22px;
              display: inline-flex; align-items: center; justify-content: center;
              background: none; border: 1px solid transparent; border-radius: 4px;
              color: #666; cursor: pointer; padding: 0; transition: all 0.12s;
            }
            .wallet-ard-arm:hover { color: #aaa; background: #22222a; border-color: #33333d; }
            .wallet-ard-arm.armed { color: #ffb300; background: #221c08; border-color: #5a4200; }
            .wallet-tb-btn.buy { color: #44cc77; background: #1a2e22; }
            .wallet-tb-btn.buy::before { background: #00ff77; }
            .wallet-tb-btn.buy:hover { color: #fff; background: #326947; border-color: #00ff77; }
            .wallet-tb-btn.sell { color: #cc4444; background: #2e1a1a; }
            .wallet-tb-btn.sell::before { background: #ff0000; }
            .wallet-tb-btn.sell:hover { color: #fff; background: #7a3333; border-color: #ff0000; }
            .wallet-tb-btn.flat { color: #e8a44d; background: #2a2218; }
            .wallet-tb-btn.flat::before { background: #e8a44d; }
            .wallet-tb-btn.flat:hover { color: #fff; background: #6a521f; border-color: #e8a44d; }
            .wallet-tb-btn.reduce { color: #44aacc; background: #18242e; }
            .wallet-tb-btn.reduce::before { background: #44aacc; }
            .wallet-tb-btn.reduce:hover { color: #fff; background: #1f527a; border-color: #44aacc; }
            /* ARD armed: steady side highlight (the lit hover look) + bot icon.
               Three classes so it wins over .buy/.sell/.flat/.reduce. */
            .wallet-tb-btn.ard-queued.ard-q-buy  { color: #fff; background: #326947; border-color: #00ff77; }
            .wallet-tb-btn.ard-queued.ard-q-sell { color: #fff; background: #7a3333; border-color: #ff0000; }
            .wallet-input {
              background: #0f0f12;
              border: 1px solid #33333d;
              border-radius: 4px;
              color: #fff;
              padding: 4px 8px;
              font-size: 11px;
              font-family: 'JetBrains Mono', monospace;
              outline: none;
              width: 80px;
              text-align: center;
              -moz-appearance: textfield;
              appearance: textfield;
            }
            .wallet-input::-webkit-outer-spin-button,
            .wallet-input::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
            .wallet-input:focus { border-color: #44444d; }
            .wallet-label {
              font-size: 9px;
              font-weight: 600;
              color: #555;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }
            .wallet-position-item {
              display: flex;
              flex-direction: column;
              padding: 6px 10px;
              border-bottom: 1px solid var(--seam, rgba(200,210,230,0.08));
              gap: 2px;
            }
            .wallet-position-item:last-child {
              border-bottom: none;
            }
            .wallet-position-row {
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .wallet-position-symbol {
              font-size: 11px;
              font-weight: 600;
              color: #c8cfdc;
              font-family: 'JetBrains Mono', monospace;
            }
            .wallet-position-side {
              font-size: 9px;
              font-weight: 700;
              letter-spacing: 0.1em;
              text-transform: uppercase;
            }
            .wallet-position-side.long  { color: var(--up, #1d9e75); }
            .wallet-position-side.short { color: var(--down, #d85a30); }
            .wallet-position-details {
              font-size: 10px;
              color: #777;
            }
            .wallet-position-pnl {
              font-size: 10px;
              font-weight: 600;
              font-family: 'JetBrains Mono', monospace;
            }
            .wallet-position-pnl.pos { color: var(--up, #1d9e75); }
            .wallet-position-pnl.neg { color: var(--down, #d85a30); }
            .wallet-close-btn {
              background: none;
              border: 1px solid #3a2020;
              color: #cc4444;
              font-size: 9px;
              font-weight: 700;
              padding: 1px 6px;
              border-radius: 3px;
              cursor: pointer;
              transition: all 0.1s;
            }
            .wallet-close-btn:hover { background: #2a1515; border-color: #cc4444; color: #ff6666; }
            .wallet-empty {
              padding: 14px 12px;
              text-align: left;
              color: rgba(154, 162, 177, 0.45);
              font-size: 12px;
              font-family: Georgia, 'Times New Roman', serif;
              font-style: italic;
              letter-spacing: 0.005em;
            }
            /* ─── Collapsible slide wrapper (grid-template-rows trick) ─── */
            .wallet-collapsible {
              display: grid;
              grid-template-rows: 1fr;
              transition: grid-template-rows 0.22s ease;
            }
            .wallet-collapsible.collapsed {
              grid-template-rows: 0fr;
            }
            .wallet-collapsible > .wallet-collapsible-inner {
              overflow: hidden;
              min-height: 0;
            }
            @keyframes wallet-spin { to { transform: rotate(360deg); } }

            /* ─── Close: the panel folds itself away ───
               Origami, LEFT TO RIGHT. Top corner in, bottom corner in, the point
               that leaves folds in — then repeat on the narrower panel, and again,
               until it's gone off the right edge (which is where the footer wallet
               icon lives, so it folds toward its own destination).

               WHY THE X AXIS. The first version creased horizontally, bottom-up —
               and you could barely see it, for a reason that's structural: by the
               time the fold runs, the collapse has squashed the panel to a ~70px
               strip. There is no height left to fold THROUGH. On phone it's worse,
               since the panel is already collapsed before you even hit the X. But
               that same strip is still FULL WIDTH. All the room is on the X axis,
               so that's where the creases go.

               THE GEOMETRY. One 5-point polygon throughout (clip-path only
               interpolates between polygons of EQUAL vertex count — mismatch it and
               the browser silently jump-cuts instead of animating):

                     (T,0) ──────── (100,0)     right edge, pinned
                       |                |
                   (P,50)              |        the point
                       |                |
                     (B,100) ────── (100,100)

               Flat is T = B = P = w. Fold the top corner and T slides right. Fold
               the bottom and B follows — now a point juts out to the left at
               (w,50). Fold THAT and P catches up, and you're flat again at a
               narrower panel. The whole animation is those three numbers taking
               turns marching right.

               THE SNAP. Every keyframe carries its own hard ease-in, and the
               animation runs linear so it doesn't fight them. This is what makes it
               read as folding rather than wiping: a default smooth morph between
               keyframes blurs the nine creases into one continuous slide. Ease-in
               per segment means each fold bends slowly, then flops over hard.

               NO OVERLAP with the collapse anymore, deliberately. The clip-path is
               in PERCENTAGES of the border box — run it while the box is still
               shrinking and every crease slides around mid-fold, which is mush. The
               collapse finishes (140ms), then the fold plays on a box that holds
               still. */
            @keyframes wallet-origami {
                0.0% { clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 50%);     animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               16.5% { clip-path: polygon(34% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 50%);    animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               29.1% { clip-path: polygon(34% 0%, 100% 0%, 100% 100%, 34% 100%, 0% 50%);   animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               40.6% { clip-path: polygon(34% 0%, 100% 0%, 100% 100%, 34% 100%, 34% 50%);  animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               51.4% { clip-path: polygon(67% 0%, 100% 0%, 100% 100%, 34% 100%, 34% 50%);  animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               61.8% { clip-path: polygon(67% 0%, 100% 0%, 100% 100%, 67% 100%, 34% 50%);  animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               71.7% { clip-path: polygon(67% 0%, 100% 0%, 100% 100%, 67% 100%, 67% 50%);  animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               81.4% { clip-path: polygon(100% 0%, 100% 0%, 100% 100%, 67% 100%, 67% 50%); animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
               90.8% { clip-path: polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%, 67% 50%); animation-timing-function: cubic-bezier(0.5, 0, 0.75, 0.15); }
              100.0% { clip-path: polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 50%); opacity: 0; }
            }
            .wallet-toolbar.wallet-closing {
              /* linear at the animation level — the per-keyframe eases above do the
                 shaping, and a second curve on top would smear them together. */
              animation: wallet-origami ${FOLD_DUR_MS}ms linear ${FOLD_DELAY_MS}ms forwards;
              pointer-events: none;   /* nothing clickable mid-fold */
            }
            /* ─── Open: the same nine creases, run backwards ───
               animation-direction: reverse. Not a second set of keyframes — the
               SAME ones, which is the only way the two can't drift apart when the
               geometry gets tweaked.
               Reverse also flips the per-keyframe timing functions, so the hard
               ease-in that made each fold SNAP shut becomes an ease-out on the way
               back: the paper springs open and settles instead of slamming. That's
               the right physics for an unfold, and it's free.
               The 'both' fill mode holds the (reversed) first frame during the
               delay and the last frame after, so there is no unclipped flash at
               either end. */
            .wallet-toolbar.wallet-opening {
              animation: wallet-origami ${FOLD_DUR_MS}ms linear 0ms reverse both;
              pointer-events: none;
            }
            /* Snappier collapse while closing. The idle double-tap collapse is a
               feature and gets its 220ms; on the way out it's just a wind-up. */
            .wallet-toolbar.wallet-closing .wallet-collapsible {
              transition-duration: ${COLLAPSE_MS}ms;
            }

            /* ─── The wake ───
               Every crease leaves the outline of the triangle it just took. Nine
               of them, each snapping on exactly as its piece departs, holding,
               then fading — so a crease pattern accumulates behind the fold and
               dissolves. It's also what makes the whole thing legible: a dark
               panel folding against a dark chart hides its own silhouette, and
               you end up watching a shape you can't see. The outlines don't have
               that problem. They ARE the contrast. */
            @keyframes fold-ghost {
                0% { opacity: 0; }
               12% { opacity: 0.85; }   /* snaps on as the piece leaves */
               42% { opacity: 0.85; }   /* holds — the delayed part of the fade */
              100% { opacity: 0; }
            }
            .wallet-fold-ghosts {
              position: fixed;
              pointer-events: none;
              z-index: 61;               /* over the folding panel, under nothing */
              overflow: visible;
            }
            .wallet-fold-ghosts polygon {
              fill: none;
              stroke: var(--accent, #f0a72b);
              stroke-width: 1;
              /* CRITICAL. preserveAspectRatio="none" stretches the 100x100 viewBox
                 non-uniformly onto a 250 x ~70 panel — roughly 2.5x on one axis and
                 0.7x on the other. Without this the stroke stretches with it and
                 you get lines that are fat horizontally and hairline vertically on
                 the SAME triangle. non-scaling-stroke renders it at a constant
                 device width, so stroke-width: 1 means one pixel, everywhere. */
              vector-effect: non-scaling-stroke;
              stroke-linejoin: round;
              opacity: 0;
              animation: fold-ghost ${GHOST_MS}ms ease-out forwards;
            }
          `}</style>

          {/* Fold ghosts. SIBLING of .wallet-toolbar, never a child — see ghostRect.
              The last one owns the unmount: the panel is visually gone when the
              fold ends, but the wake is still fading, and firing finishClose on
              the fold's animationend would yank the outlines off mid-dissolve. */}
          {/* Only the CLOSE hands off to finishClose. On open the timer owns the
              finish — and data-last is the FIRST ghost to fire there anyway, since
              the last piece to fold away is the first to come back. */}
          {(panelClosing || panelOpening) && ghostRect && (
            <svg
              className="wallet-fold-ghosts"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden="true"
              style={{ left: ghostRect.left, top: ghostRect.top, width: ghostRect.width, height: ghostRect.height }}
              onAnimationEnd={(e) => { if (panelClosing && e.target?.dataset?.last === '1') finishClose() }}
            >
              {FOLD_GHOSTS.map((g, i) => {
                // Mirror each crease's moment when unfolding: the piece that left
                // LAST is the piece that returns FIRST. Same triangles, same
                // keyframes — only the delay flips.
                const at = panelOpening ? 1 - g.at : g.at
                // No FOLD_DELAY_MS offset. Closing, this SVG doesn't mount until
                // the fold actually begins (the timer above already spent that
                // delay). Opening, the unfold starts immediately. Either way t=0
                // for this overlay IS the fold's 0%.
                const delay = at * FOLD_DUR_MS
                return (
                  <polygon
                    key={i}
                    points={g.pts}
                    data-last={i === FOLD_GHOSTS.length - 1 ? '1' : undefined}
                    style={{ animationDelay: `${delay}ms` }}
                  />
                )
              })}
            </svg>
          )}

          <div
            ref={menuRef}
            className={`wallet-toolbar${asPanel ? ' wallet-toolbar-panel' : ''}${panelClosing ? ' wallet-closing' : ''}${panelOpening ? ' wallet-opening' : ''}`}
            style={asPanel
              ? { position: 'static', left: 'auto', top: 'auto', width: '100%', height: '100%', maxHeight: '100%' }
              : held
                ? { position: 'absolute', left: panelX, top: panelY }
                : { left: panelX, top: panelY }}
          >
            {/* Drag handle + title + close */}
            <div
              className="wallet-menu-titlebar"
              onMouseDown={handleDragStart}
              onTouchStart={asPanel ? undefined : handleTitleTouchStart}
              onDoubleClick={asPanel ? undefined : () => setCollapsed(c => !c)}
              title={asPanel ? undefined : (collapsed ? 'Double-click to expand' : 'Double-click to collapse')}
              style={asPanel ? undefined : { touchAction: 'none' }}
            >
              <div className="wallet-menu-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {/* ARD arm — moved out of the dials row into the TITLEBAR, far
                    left, so it stays reachable while the panel is COLLAPSED
                    (the dials row is inside .wallet-collapsible and hides; the
                    titlebar never does).
                    stopPropagation on mousedown/doubleclick is required here:
                    the titlebar is the drag handle and double-click toggles
                    collapse, so without it arming the bot would drag the panel
                    or collapse it. */}
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                  {/* The ARD arm control moved onto BUY/SELL as a long
                      press. A live trigger no longer sits in a row of
                      passive readouts, and the bot mark now appears only
                      while it means something. */}
                </div>
                <img src="/Wallet1.png" alt="wallet" style={{ width: '14px', height: '14px', opacity: 0.7 }} />
                {/* Map of the connected wallet. stopPropagation: the titlebar
                    is the drag handle and double-click collapses the panel. */}
                <button
                  ref={mapBtnRef}
                  className="holdings-map-btn"
                  disabled={!publicKey || isFutures}
                  title={publicKey && !isFutures ? 'Open wallet map' : 'Connect a Solana wallet to see its map'}
                  aria-label="Open wallet map"
                  aria-expanded={mapOpen}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  onDoubleClick={(e) => e.stopPropagation()}
                  onClick={() => setMapOpen(o => !o)}
                ><WalletMapIcon size={13} /></button>
                <span>{walletLocked ? '🔒 Locked' : isStk ? 'Schwab' : isFutures ? (hlAvailable > 0 ? `$${hlAvailable.toFixed(2)} USDC` : 'Futures') : (walletBalance !== null ? `${walletBalance.toFixed(4)} SOL` : '---')}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {!asPanel && (
                  <button
                    className="wallet-menu-close"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={startClose}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            {mapOpen && publicKey && !isFutures && (
              <HoldingsMapPopover address={publicKey.toString()} anchorRef={mapBtnRef} onClose={closeMap} />
            )}

            {/* Phone: BUY/SELL directly UNDER the header (spec). Desktop
                keeps the row in its normal body position below. */}
            {asPanel && (
              <div className="wallet-panel-topbtns">{buySellRow}</div>
            )}

            {/* ─── Controls: knobs top + buttons/qty below ─── */}
            <div style={{ display: 'flex', flexDirection: 'column' }}
              data-controls-section=""
              onContextMenu={onControlsContextMenu}
              onPointerDownCapture={onControlsPointerDown}
              onPointerMoveCapture={onControlsPointerMove}
              onPointerUpCapture={cancelCtlPress}
              onPointerCancelCapture={cancelCtlPress}
              onClickCapture={onControlsClick}>

              {/* Knob row — Solana: QTY / BUY% / SELL%   Futures: QTY / LEV / SELL% */}
              <div className={`wallet-collapsible ${collapsed ? 'collapsed' : ''}`}>
                <div className="wallet-collapsible-inner">
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                position: 'relative',
                padding: '6px 6px',
                borderBottom: '1px solid #2a2a35',
              }}>
                {/* Churn gauge — pinned left on desktop; on phone it joins the
                    flex flow so it sits right next to the three controls
                    instead of floating at the far-left edge. */}
                <div style={asPanel
                  ? { position: 'static', transform: 'none' }
                  : { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                  <BarGauge
                    label="CHURN"
                    value={Math.min(botMetrics.churnScore ?? 0, 10)}
                    max={10}
                    segments={7}
                    colors={['#e8d44d', '#44cc77']}
                  />
                </div>

                {isFutures ? (() => {
                  // QTY is now MARGIN in USD. Order notional = qty × leverage.
                  // Cap QTY to available USDC; cap LEV to per-product max from hlPair.
                  const maxLev = (hlPair?.maxLeverage && Number.isFinite(hlPair.maxLeverage)) ? hlPair.maxLeverage : 50
                  const maxQty = isCme ? Math.max(0, Math.floor(cmeAccount(cmeSym, liveMcap).available)) : hlAvailable > 0 ? Math.floor(hlAvailable * 100) / 100 : 5000

                  // Build snap points as fractions of available margin so the knob always
                  // spans the full usable range no matter how much money you have.
                  const qtyFractions = [0.05, 0.10, 0.25, 0.50, 0.75, 1.00]
                  const qtySnaps = Array.from(new Set(
                    qtyFractions.map(f => {
                      const v = maxQty * f
                      // Round small values to cents, large values to whole dollars
                      return v >= 100 ? Math.round(v) : Math.round(v * 100) / 100
                    })
                  )).filter(v => v > 0).sort((a, b) => a - b)
                  if (qtySnaps.length === 0) qtySnaps.push(maxQty)

                  // Same fractional approach for leverage so it scales to per-product cap
                  const levFractions = [0.02, 0.05, 0.10, 0.20, 0.40, 0.60, 0.80, 1.00]
                  const levSnaps = Array.from(new Set(
                    levFractions.map(f => Math.max(1, Math.round(maxLev * f)))
                  )).sort((a, b) => a - b)
                  if (levSnaps.length === 0) levSnaps.push(maxLev)

                  // Clamp current values into the new bounds (display only)
                  const curQty = Math.min(defaults.futuresQty || 0, maxQty)
                  const curLev = Math.min(defaults.futuresLeverage ?? 10, maxLev)
                  return (
                    <>
                      <ControlInput styleKind={activeControlStyle} onRequestMenu={(mx, my) => setControlMenu({ x: mx, y: my })}
                        label="Risk"
                        color="#44cc77"
                        value={curQty}
                        min={0}
                        max={maxQty}
                        snapPoints={qtySnaps}
                        onChange={(v) => setFutQty(parseFloat(Math.min(v, maxQty).toFixed(4)))}
                        formatValue={(v) => v >= 1000 ? `$${(v/1000).toFixed(1)}k` : v >= 10 ? `$${Math.round(v)}` : `$${v.toFixed(2)}`}
                      />
                      {unitMode ? (() => {
                        // remote-units-20261104: center fader = shares (stock) or
                        // contracts (CME/option); stocks capped at Risk $ / last.
                        const px = cmePxRef.current || 0
                        const uMax = isStk && !isStkOpt && px > 0 ? Math.max(1, Math.floor(curQty / px)) : 100
                        return (
                          <ControlInput styleKind={activeControlStyle} onRequestMenu={(mx, my) => setControlMenu({ x: mx, y: my })}
                            label={qtyUnit === 'sh' ? 'SH' : 'CT'}
                            color="#e8a44d"
                            value={Math.min(displayedQty, uMax)}
                            min={1}
                            max={uMax}
                            step={1}
                            onChange={(v) => setCenterQty(Math.min(Math.round(v), uMax))}
                            formatValue={(v) => `${Math.round(v)}${qtyUnit}`}
                          />
                        )
                      })() : (
                      <ControlInput styleKind={activeControlStyle} onRequestMenu={(mx, my) => setControlMenu({ x: mx, y: my })}
                        label="LEV"
                        color="#e8a44d"
                        value={curLev}
                        min={1}
                        max={maxLev}
                        snapPoints={levSnaps}
                        onChange={(v) => setDefaults({ futuresLeverage: Math.min(Math.round(v), maxLev) })}
                        formatValue={(v) => `${Math.round(v)}x`}
                      />)}
                      <ControlInput styleKind={activeControlStyle} onRequestMenu={(mx, my) => setControlMenu({ x: mx, y: my })}
                        label="SELL"
                        color="#cc4444"
                        value={defaults.sellPercentage}
                        min={10}
                        max={100}
                        snapPoints={[10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100]}
                        onChange={(v) => setDefaults({ sellPercentage: Math.round(v) })}
                        formatValue={(v) => `${Math.round(v)}%`}
                      />
                    </>
                  )
                })() : (
                  <>
                    <ControlInput styleKind={activeControlStyle} onRequestMenu={(mx, my) => setControlMenu({ x: mx, y: my })}
                      label="QTY"
                      color="#44cc77"
                      value={defaults.buyAmountSol}
                      min={0}
                      max={walletBalance || 1}
                      step={0.001}
                      onChange={(v) => setDefaults({ buyAmountSol: parseFloat(v.toFixed(4)) })}
                      formatValue={(v) => v.toFixed(3)}
                    />
                    <ControlInput styleKind={activeControlStyle} onRequestMenu={(mx, my) => setControlMenu({ x: mx, y: my })}
                      label="BUY"
                      color="#44aacc"
                      value={walletBalance > 0 ? (defaults.buyAmountSol / walletBalance) * 100 : 0}
                      min={10}
                      max={100}
                      snapPoints={[10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100]}
                      onChange={(pct) => {
                        if (walletBalance > 0) {
                          const sol = parseFloat(((pct / 100) * walletBalance).toFixed(4))
                          setDefaults({ buyAmountSol: Math.max(0.001, sol) })
                        }
                      }}
                      formatValue={(v) => `${Math.round(v)}%`}
                    />
                    <ControlInput styleKind={activeControlStyle} onRequestMenu={(mx, my) => setControlMenu({ x: mx, y: my })}
                      label="SELL"
                      color="#cc4444"
                      value={defaults.sellPercentage}
                      min={10}
                      max={100}
                      snapPoints={[10, 20, 25, 30, 40, 50, 60, 70, 75, 80, 90, 100]}
                      onChange={(v) => setDefaults({ sellPercentage: Math.round(v) })}
                      formatValue={(v) => `${Math.round(v)}%`}
                    />
                  </>
                )}

                {/* MKT / LMT — vertical switch, upright labels, right of the dials.
                    The switch itself is a horizontal 100x60 control, so it is
                    ROTATED -90deg rather than rebuilt. Its OWN labels are
                    suppressed (showLabels={false}) and re-rendered here as
                    plain upright spans — rotating the whole component would
                    have turned the text on its side too. LMT on top / MKT on
                    the bottom matches the sell-high / buy-low sense of the
                    dials beside it.
                    The rotated switch lives in a wrapper sized with SWAPPED
                    dimensions (60x100 scaled instead of 100x60) because a bare
                    transform does not change layout — without it the parent
                    would reserve the unrotated box and the switch would overlap
                    the SELL dial. The column is centred vertically in the row. */}
                <div style={asPanel
                  ? { position: 'static', alignSelf: 'center' }
                  : { position: 'absolute',
                      // Sliders are narrower than dials, so the switch can sit
                      // closer to the edge; with dials it needs to clear the SELL
                      // knob or it overlaps it. One offset per control style.
                      right: activeControlStyle === 'dial' ? '8px' : '23px',
                      top: '50%', transform: 'translateY(-50%)' }}>
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: '3px',
                  }}>
                    <span style={{
                      fontSize: 8, fontWeight: 700, letterSpacing: 0.5,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: orderMode === 'limit' ? '#ffb020' : '#555',
                      transition: 'color 0.25s',
                    }}>LMT</span>

                    <div style={{ width: 60 * 0.42, height: 100 * 0.42, position: 'relative' }}>
                      <div style={{
                        position: 'absolute', top: 0, left: 0,
                        transform: 'rotate(-90deg) translateX(-100%)',
                        transformOrigin: 'top left',
                      }}>
                        <MarketLimitToggle
                          mode={orderMode}
                          onChange={setOrderMode}
                          scale={0.42}
                          showLabels={false}
                        />
                      </div>
                    </div>

                    <span style={{
                      fontSize: 8, fontWeight: 700, letterSpacing: 0.5,
                      fontFamily: "'JetBrains Mono', monospace",
                      color: orderMode === 'limit' ? '#555' : '#ffb020',
                      transition: 'color 0.25s',
                    }}>MKT</span>
                    {isStk && <span role="button" title="Schwab order duration (click to toggle)" onMouseDown={(e) => e.stopPropagation()} onClick={() => setTif(stkTif === 'gtc' ? 'market' : 'limit')} style={{ fontSize: 8, fontWeight: 700, letterSpacing: 0.5, fontFamily: "'JetBrains Mono', monospace", color: '#2f6fd6', textDecoration: 'underline', cursor: 'pointer', marginTop: 7, userSelect: 'none' }}>{stkTif === 'gtc' ? 'GTC' : 'DAY'}</span>}
                  </div>
                </div>

              </div>
                </div>
              </div>

              {/* Buttons + qty */}
              <div>

                {/* Buy/Sell Row — desktop only; phone renders it at the very top. */}
                {!asPanel && buySellRow}

                {/* ── QTY ROW — HIDDEN (kept for easy restore) ──
                    Removed from the desktop layout: the row that sat between the
                    BUY/SELL buttons and the token list (Qty label + number input +
                    knurled wheel + MKT/LMT switch). The MKT/LMT switch moved up to
                    the dials row as a vertical toggle; the qty input itself is still
                    rendered on PHONE (asPanel) in the titlebar, which is a separate
                    call site and is untouched.
                    To restore: delete this comment wrapper and the closing marker.
                                    {/* QTY Row *\/}
                                    <div className={`wallet-collapsible ${collapsed ? 'collapsed' : ''}`}>
                                      <div className="wallet-collapsible-inner">
                                    <div className="wallet-toolbar-row" style={{ padding: '4px 6px' }}>
                                      <span className="wallet-label">{isFutures ? 'Mgn' : 'Qty'}</span>
                                      {qtyInput}
                                      {/*!asPanel && <span style={{ fontSize: '10px', color: '#666' }}>{isFutures ? '$' : 'SOL'}</span>*\/}
                                      {isFutures && (() => {
                                        const lev = defaults.futuresLeverage ?? 10
                                        const notional = (defaults.futuresQty || 0) * lev
                                        return (
                                          <span style={{ fontSize: '9px', color: '#888', marginLeft: '6px', fontFamily: "'JetBrains Mono', monospace" }}>
                                            →{notional >= 1000 ? `$${(notional/1000).toFixed(1)}k` : `$${Math.round(notional)}`}
                                          </span>
                                        )
                                      })()}
                                    </div>
                                      </div>
                                    </div>
                */}
              </div>
            </div>

            {/* Positions / Holdings List — full width below */}
            <div className={`wallet-collapsible ${collapsed ? 'collapsed' : ''}`}>
              <div className="wallet-collapsible-inner">
            {/* List header — refresh pinned top-right, stays above the scroll area */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '2px 6px 3px' }}>
              <button
                style={{
                  background: 'none',
                  border: '1px solid transparent',
                  color: '#555',
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: '2px 5px',
                  borderRadius: '3px',
                  lineHeight: 1,
                  transition: 'color 0.1s, border-color 0.1s',
                  animation: loading ? 'wallet-spin 0.6s linear infinite' : 'none',
                }}
                onMouseEnter={e => { e.target.style.color = '#aaa'; e.target.style.borderColor = '#333' }}
                onMouseLeave={e => { e.target.style.color = '#555'; e.target.style.borderColor = 'transparent' }}
                onClick={fetchHoldings}
                title={isFutures ? 'Refresh positions' : 'Refresh holdings'}
              >⟳</button>
            </div>
            <div
              className="wallet-positions-list wallet-positions-list-scroll"
              style={{
                maxHeight: '420px',
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                scrollbarColor: '#111 #050505'
              }}
            >
              {/* ─── Futures: HL positions + open orders ─── */}
              {isCme && (() => {
                const upnl = cmeUnrealized(cmePos, liveMcap)
                const rpnl = cmeRealized(cmeSym)
                const sgn = (n) => `${n >= 0 ? '+' : '-'}${formatUsd(Math.abs(n))}`
                return (
                  <>
                    <div className="wallet-position-item">
                      <div className="wallet-position-row">
                        <span className="wallet-position-symbol">{cmeSym} <span style={{ color: '#e8a44d', fontWeight: 700, fontSize: 9, letterSpacing: '0.06em' }}>PAPER</span></span>
                        {cmePos && (
                          <button className="wallet-close-btn" title="Flatten (paper)"
                            onClick={(e) => { e.stopPropagation(); try { const r = cmeFlatten(cmeSym, liveMcap); showToast?.(`PAPER FLAT ${cmeSym} @ ${liveMcap} · ${sgn(r.realized)}`, 'sell') } catch (err) { showToast?.(`Paper flatten failed: ${err.message}`, 'sell') } }}
                          >X</button>
                        )}
                      </div>
                      {cmePos ? (
                        <div className="wallet-position-row">
                          <span>
                            <span className={`wallet-position-side ${cmePos.side === 'LONG' ? 'long' : 'short'}`}>{cmePos.side === 'LONG' ? 'L' : 'S'}</span>
                            <span className="wallet-position-details" style={{ marginLeft: '4px' }}>{cmePos.qty} @ {cmePos.avg.toFixed(2)}</span>
                          </span>
                          <span className={`wallet-position-pnl ${upnl >= 0 ? 'pos' : 'neg'}`}>{sgn(upnl)}</span>
                        </div>
                      ) : <div className="wallet-empty">flat</div>}
                      <div className="wallet-position-row">
                        <span className="wallet-position-details">realized · ${pointValue(cmeSym)}/pt</span>
                        <span className={`wallet-position-pnl ${rpnl >= 0 ? 'pos' : 'neg'}`}>{sgn(rpnl)}</span>
                      </div>
                      {(() => { const a = cmeAccount(cmeSym, liveMcap); return (<>
                        <div className="wallet-position-row">
                          <span className="wallet-position-details">equity · bal {formatUsd(a.cash)}</span>
                          <span className="wallet-position-details">{formatUsd(a.equity)}</span>
                        </div>
                        <div className="wallet-position-row">
                          <span className="wallet-position-details">margin {formatUsd(a.used)} · avail {formatUsd(Math.max(0, a.available))}</span>
                          <button className="wallet-close-btn" title="Reset paper account to $10,000"
                            onClick={(e) => { e.stopPropagation(); if (window.confirm('Reset CME paper account to $10,000? Clears positions and history.')) { cmeResetAccount(); showToast?.('PAPER account reset', 'buy') } }}
                          >↺</button>
                        </div>
                      </>) })()}
                    </div>
                  </>
                )
              })()}
              {isFutures && !isCme && (
                <>
                  {hlPositions.length === 0 && hlOrders.length === 0 && (
                    <div className="wallet-empty">no open positions</div>
                  )}
                  {/* Positions */}
                  {hlPositions.map((p) => {
                    // Live P/L for the position whose symbol matches the
                    // currently-charted pair. Same recipe FootprintChart's
                    // position tag uses: (currentPx - entryPx) * qty with
                    // sign flip for shorts. Re-runs every render because
                    // liveMcap is state. Falls back to the polled
                    // unrealizedPNL when this position isn't the one
                    // being charted, or before any tick has landed.
                    const isCurrentSymbol = !!hlPair?.base && p.symbol === hlPair.base
                    const { pnlUsd: pnl } = futuresPositionPnl({
                      entryPx: p.avgOpenPrice,
                      currentPx: isCurrentSymbol ? liveMcap : 0,
                      qty: p.qty,
                      side: p.side,
                      fallbackUsd: p.unrealizedPNL,
                    })
                    return (
                      <div key={`pos-${p.symbol}`} className="wallet-position-item">
                        <div className="wallet-position-row">
                          <span className="wallet-position-symbol">{p.symbol}</span>
                          <button
                            className="wallet-close-btn"
                            onClick={(e) => { e.stopPropagation(); handleCloseHlPosition(p) }}
                            disabled={closingPos === p.symbol}
                          >
                            {closingPos === p.symbol ? '...' : 'X'}
                          </button>
                        </div>
                        <div className="wallet-position-row">
                          <span>
                            <span className={`wallet-position-side ${p.side === 'LONG' ? 'long' : 'short'}`}>
                              {p.side === 'LONG' ? 'L' : 'S'}
                            </span>
                            <span className="wallet-position-details" style={{ marginLeft: '4px' }}>
                              {p.qty} @ ${p.avgOpenPrice?.toFixed?.(2) || p.avgOpenPrice}
                            </span>
                          </span>
                          <span className={`wallet-position-pnl ${pnl >= 0 ? 'pos' : 'neg'}`}>
                            {pnl >= 0 ? '+' : ''}{formatUsd(Math.abs(pnl))}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                  {/* Open Orders */}
                  {hlOrders.map((o) => (
                    <div key={`ord-${o.orderId}`} className="wallet-position-item">
                      <div className="wallet-position-row">
                        <span className="wallet-position-symbol">{o.symbol}</span>
                        <button
                          className="wallet-close-btn"
                          onClick={(e) => { e.stopPropagation(); handleCancelHlOrder(o) }}
                          disabled={cancellingId === o.orderId}
                          style={{ borderColor: '#444', color: '#888' }}
                        >
                          {cancellingId === o.orderId ? '...' : 'X'}
                        </button>
                      </div>
                      <div className="wallet-position-row">
                        <span>
                          <span className={`wallet-position-side ${o.side === 'buy' ? 'long' : 'short'}`}>
                            {o.side?.toUpperCase?.() || o.side}
                          </span>
                          <span className="wallet-position-details" style={{ marginLeft: '4px' }}>
                            {o.qty} @ ${parseFloat(o.price).toFixed(2)}
                          </span>
                        </span>
                        <span className="wallet-position-details">{o.orderType || 'LIMIT'}</span>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {/* ─── Solana: token holdings + limit orders ─── */}
              {!isFutures && (
                <>
                  {loading && holdings.length === 0 && !tradingPos && (
                    <div className="wallet-empty">Loading...</div>
                  )}
                  {!loading && holdings.length === 0 && !tradingPos && solanaLimitOrders.length === 0 && (
                    <div className="wallet-empty">no tokens held</div>
                  )}
                  {currentHolding && (
                    <div className="wallet-position-item" style={{ background: '#0f0f12' }}>
                      <div className="wallet-position-row">
                        <span className="wallet-position-symbol">{currentHolding.name}</span>
                        <button
                          className="wallet-close-btn"
                          onClick={(e) => { e.stopPropagation(); handleClose(currentHolding) }}
                          disabled={closing === currentHolding.mint}
                        >
                          {closing === currentHolding.mint ? '...' : 'X'}
                        </button>
                      </div>
                      <HoldingDetailRows d={currentDetails} formatAmount={formatAmount} formatUsd={formatUsd} formatPnl={formatPnl} />
                      {(lpByMint.get(currentHolding.mint) || []).map((lp, i, arr) => (
                        <LpLine key={lpKey(lp, i)} lp={lp} formatUsd={formatUsd} formatAmount={formatAmount} showName={arr.length > 1} />
                      ))}
                      <div className="wallet-lp-actions">
                        <button
                          className="wallet-lp-add"
                          onClick={(e) => { e.stopPropagation(); setLpOpenMint(m => m === currentHolding.mint ? null : currentHolding.mint) }}
                        >{lpOpenMint === currentHolding.mint ? '− LP' : '+ LP'}</button>
                      </div>
                      {lpOpenMint === currentHolding.mint && (
                        <MeteoraLpPanel mint={currentHolding.mint} symbol={currentHolding.name}
                          uiBalance={walletHolding?.uiAmount ?? null} refreshKey={holdingsTick}
                          onClose={() => setLpOpenMint(null)} />
                      )}
                    </div>
                  )}
                  {/* Solana limit orders */}
                  {solanaLimitOrders.length > 0 && (
                    <>
                      <div style={{ padding: '4px 8px 2px', fontSize: '9px', fontWeight: 600, color: '#555', letterSpacing: '0.08em', textTransform: 'uppercase', borderBottom: '1px solid #2a2a35' }}>
                        Limit Orders
                      </div>
                      {solanaLimitOrders.map((lo) => (
                        <div key={lo.id} className="wallet-position-item">
                          <div className="wallet-position-row">
                            <span>
                              <span className={`wallet-position-side ${lo.side === 'buy' ? 'long' : 'short'}`}>
                                {lo.side.toUpperCase()}
                              </span>
                              <span className="wallet-position-details" style={{ marginLeft: '4px' }}>
                                @ {formatMcapCompact(lo.targetMcap)}
                              </span>
                            </span>
                            <button
                              className="wallet-close-btn"
                              onClick={(e) => { e.stopPropagation(); handleCancelSolanaLimit(lo) }}
                              disabled={cancellingLimitId === lo.id}
                              style={{ borderColor: '#444', color: '#888' }}
                            >
                              {cancellingLimitId === lo.id ? '...' : 'X'}
                            </button>
                          </div>
                          <div className="wallet-position-row">
                            <span className="wallet-position-details">
                              {lo.amount ? `${lo.amount} SOL` : `${lo.percentage}%`}
                            </span>
                            <span className="wallet-position-details">{lo.ocoType === 'tp' ? 'TP' : lo.ocoType === 'sl' ? 'SL' : 'LIMIT'}</span>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {/* ─── Meteora LP positions ─── */}
                  {lpOrphans.length > 0 && (
                    <>
                      <div className="wallet-lp-head">
                        <span>LP POSITIONS</span>
                        <button className="wallet-lp-refresh" title="Refresh LP data"
                          onClick={() => { refreshLpPositions(publicKey?.toString()); }}>↻</button>
                      </div>
                      {lpOrphans.map((lp, i) => (
                        <div key={lpKey(lp, i)} className="wallet-position-item wallet-lp-item">
                          <div className="wallet-position-row">
                            <span className="wallet-position-symbol"
                              style={lp.mintX ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#333' } : undefined}
                              onClick={lp.mintX ? (() => window.dispatchEvent(new CustomEvent('solcheddar-navigate-token', { detail: { address: lp.mintX } }))) : undefined}
                            ><span className="wallet-lp-tag">{lp.kind === 'v2' ? 'v2' : 'DLMM'}</span>{lp.name || (lp.pool ? `${lp.pool.slice(0, 4)}…${lp.pool.slice(-4)}` : 'LP')}</span>
                            {lp.inRange != null && (
                              <span className={`wallet-lp-range ${lp.inRange ? 'in' : 'out'}`}
                                title={lp.inRange
                                  ? 'Active bin inside your range — position is earning fees'
                                  : 'Price left your range — position is NOT earning until it returns or you rebalance'}>
                                {lp.inRange ? 'IN RANGE' : 'OUT'}
                              </span>
                            )}
                          </div>
                          <div className="wallet-position-row">
                            <span className="wallet-position-details">
                              <LpAmounts lp={lp} formatUsd={formatUsd} formatAmount={formatAmount} />
                              {lp.feesUsd != null && <span className="wallet-lp-fees"> · fees {formatUsd(lp.feesUsd)}</span>}
                            </span>
                            {lp.pnlUsd != null && (
                              <span className={`wallet-position-pnl ${lp.pnlUsd >= 0 ? 'pos' : 'neg'}`}>
                                {formatPnl(lp.pnlUsd)}
                              </span>
                            )}
                          </div>
                          <LpManage lp={lp} />
                        </div>
                      ))}
                    </>
                  )}
                  {lpError && (
                    <div className="wallet-lp-note">
                      {lpError}
                      <button className="wallet-lp-refresh" title="Retry LP data"
                        onClick={() => { refreshLpPositions(publicKey?.toString()); }}>retry ↻</button>
                    </div>
                  )}
                  {/* Other holdings */}
                  {otherHoldings.map((t) => (
                    <div key={t.mint} className="wallet-position-item">
                      <div className="wallet-position-row">
                        <span className="wallet-position-symbol" style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#333' }}
                          onClick={() => window.dispatchEvent(new CustomEvent('solcheddar-navigate-token', { detail: { address: t.mint } }))}
                          >{t.name}</span>
                        <button
                          className="wallet-close-btn"
                          onClick={(e) => { e.stopPropagation(); handleClose(t) }}
                          disabled={closing === t.mint}
                        >
                          {closing === t.mint ? '...' : 'X'}
                        </button>
                      </div>
                      <HoldingDetailRows d={holdingDetails(t, liveSolUsd)} formatAmount={formatAmount} formatUsd={formatUsd} formatPnl={formatPnl} />
                      {(lpByMint.get(t.mint) || []).map((lp, i, arr) => (
                        <LpLine key={lpKey(lp, i)} lp={lp} formatUsd={formatUsd} formatAmount={formatAmount} showName={arr.length > 1} />
                      ))}
                      <div className="wallet-lp-actions">
                        <button
                          className="wallet-lp-add"
                          onClick={(e) => { e.stopPropagation(); setLpOpenMint(m => m === t.mint ? null : t.mint) }}
                        >{lpOpenMint === t.mint ? '− LP' : '+ LP'}</button>
                      </div>
                      {lpOpenMint === t.mint && (
                        <MeteoraLpPanel mint={t.mint} symbol={t.name} uiBalance={t.uiAmount}
                          refreshKey={holdingsTick} onClose={() => setLpOpenMint(null)} />
                      )}
                    </div>
                  ))}
                </>
              )}
            </div>
              </div>
            </div>

          </div>
        </>
      )}
    </>
  )
}
